import { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '../lib/useIsMobile.js';
import { api } from '../lib/api.js';
import Icon from './Icon.jsx';
import ChatThemes from './ChatThemes.jsx'; // themes across all chats (flag fanowl.themes)
import { Channels } from './FanOwlChannels.jsx'; // the per-channel cards — shared with the Conversations page
import FanOwlWhatsapp from './FanOwlWhatsapp.jsx'; // 💬 the same Owl over WhatsApp — its own panel, its own routes

// ─── Fan Owl config — dual-surface editor (docs/specs/FAN_OWL_SPEC.md §3B) ─────
// The same component serves Admin → client detail (scope="admin-client") and the
// client's own Settings (scope="my"), like MailTemplateEditor. Organised as four
// sections: SITES & PAGES (embed key, domain allowlist, page mappings with their
// per-page info + chips, the website reader), CATALOGUE (tickets/add-ons with
// Howler-supplied buy links + images — the Owl's only price facts), FAQs
// (event-wide knowledge the Owl may quote), and REPORTS (funnel, FAQ gaps,
// captured fans).

const PAGE_TYPES = ['home', 'lineup', 'artist', 'tickets', 'attraction', 'venue', 'accommodation', 'sponsors', 'faq', 'other'];
const ITEM_KINDS = ['ticket', 'addon', 'bundle', 'accommodation', 'transport', 'merchandise'];
const AVAILABILITY = ['', 'selling fast', 'last few', 'sold out', 'coming soon'];
const LANGS = [['', "Auto — fan's device language, else English"], ['en', 'English'], ['af', 'Afrikaans'], ['it', 'Italiano'], ['es', 'Español'], ['fr', 'Français'], ['de', 'Deutsch'], ['pt', 'Português'], ['nl', 'Nederlands']];
const NAV_TYPE_LABELS = { home: 'Home', tickets: 'Tickets', lineup: 'Line-up', artist: 'Artists', venue: 'Venue', accommodation: 'Stay', attraction: 'Explore', sponsors: 'Partners', faq: 'FAQs', other: 'More' };
// Same derivation as the server: a mapping is navigable when its pattern leaves a path.
// The Howler super app boots the Owl with synthetic URLs (app://event/<id>/<screen>),
// so each app screen can carry its own mapping — same machinery as website pages.
// Includes app-only screens that have no website equivalent (wallet, feed, chat).
const APP_SCREENS = [
  { urlPattern: 'app://*/store', pageType: 'tickets', note: 'Howler app — event store (tickets)' },
  { urlPattern: 'app://*/lineup', pageType: 'lineup', note: 'Howler app — line-up screen' },
  { urlPattern: 'app://*/info', pageType: 'faq', note: 'Howler app — event info screen' },
  { urlPattern: 'app://*/map', pageType: 'venue', note: 'Howler app — venue map screen' },
  { urlPattern: 'app://*/explore', pageType: 'home', note: 'Howler app — explore / event landing' },
  { urlPattern: 'app://*/wallet', pageType: 'other', note: 'Howler app — My Tickets / wallet (app-only)' },
  { urlPattern: 'app://*/feed', pageType: 'other', note: 'Howler app — event feed (app-only)' },
  { urlPattern: 'app://*/chat', pageType: 'other', note: 'Howler app — event chat (app-only)' },
];
const navigablePath = (pattern) => {
  if (/^app:/i.test(String(pattern || '').trim())) return ''; // app screens are context-only, never website nav
  const frag = String(pattern || '').replace(/\*/g, '').replace(/[^\w/\-.:]/g, '').trim();
  return !frag || frag.startsWith('/') ? frag : `/${frag}`;
};
const input = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', border: '1.5px solid var(--hairline)', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', background: 'var(--card, #fff)', color: 'var(--text)' };
const small = { fontSize: 11.5, color: 'var(--muted)', margin: '2px 0 4px' };
const btn = { padding: '8px 14px', borderRadius: 8, border: '1.5px solid var(--hairline)', background: 'transparent', color: 'var(--text)', fontSize: 12.5, cursor: 'pointer', minHeight: 36 };
const primaryBtn = { ...btn, background: 'var(--text)', color: 'var(--bg, #fff)', border: 0, fontWeight: 700 };
const card = { border: '1px solid var(--hairline)', borderRadius: 12, padding: 14, marginTop: 10 };
const H = ({ children }) => <h3 style={{ fontSize: 14, fontWeight: 700, margin: '18px 0 2px' }}>{children}</h3>;
const linkBtn = { background: 'none', border: 0, padding: 0, color: 'var(--text)', textDecoration: 'underline', cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit', minHeight: 24 };
const summaryStyle = { cursor: 'pointer', fontWeight: 700, fontSize: 13, padding: '8px 0', listStyle: 'none' };

export default function FanOwlAdmin({ scope = 'admin-client', entityId }) {
  const isMobile = useIsMobile();
  const base = scope === 'my' ? `/api/my/fan-owl/${entityId}` : `/api/admin/entities/${entityId}/fan-owl`;
  const syncBase = scope === 'my' ? `/api/my/fan-owl-sync/${entityId}` : `/api/admin/entities/${entityId}/fan-owl-sync`; // scheduled re-reads (server/fanOwlSync.js)
  const [cfg, setCfg] = useState(null);
  const [suites, setSuites] = useState([]);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [ingestUrl, setIngestUrl] = useState('');
  const [ingestSuite, setIngestSuite] = useState(''); // which event the site section being read belongs to
  const [ingesting, setIngesting] = useState(false);
  const [ingestNote, setIngestNote] = useState('');
  const [tab, setTab] = useState('sites');
  const [imgBusy, setImgBusy] = useState(-1); // catalogue index mid-upload
  const [catGroupOpen, setCatGroupOpen] = useState({}); // multi-event catalogue: which event sections are expanded (key = suiteId | '')
  const [imgNote, setImgNote] = useState(null); // { i, text }
  const [avBusy, setAvBusy] = useState(-1); // site index mid-avatar-upload
  const [ticketUrl, setTicketUrl] = useState('');
  const [ticketSuite, setTicketSuite] = useState(''); // which event the ticket link being read belongs to
  const [catIngesting, setCatIngesting] = useState(false);
  const [catNote, setCatNote] = useState('');
  const [navBusy, setNavBusy] = useState(false);
  const [navNote, setNavNote] = useState(null); // { i, text }
  const [syncPending, setSyncPending] = useState(0); // Auto-update: re-reads waiting for a person (badge on the tab)
  const TABS = [['sites', '🌐 Sites'], ['persona', '🪄 Personality'], ['nav', '🧭 Navigation'], ['pages', '📄 Pages'], ['catalogue', '🎟️ Catalogue'], ['knowledge', '❓ FAQs'], ['sync', `🔄 Auto-update${syncPending ? ` (${syncPending})` : ''}`], ['whatsapp', '💬 WhatsApp'], ['rewards', '🎁 Rewards'], ['reports', '📊 Reports']];
  // Reward pools (loyalty phase 2) — their own routes; 403 = the fanowl.loyalty
  // flag is off for this client, and the tab explains that instead of erroring.
  const loyaltyBase = scope === 'my' ? `/api/my/loyalty/${entityId}` : `/api/admin/entities/${entityId}/loyalty`;
  const [pools, setPools] = useState(null);
  const [poolsDenied, setPoolsDenied] = useState(false);
  const [poolsSaving, setPoolsSaving] = useState(false);
  const [poolsSavedAt, setPoolsSavedAt] = useState(0);
  const [poolsError, setPoolsError] = useState(''); // a refused save used to vanish silently — the box looked saved, reload showed the old state
  const [codesDraft, setCodesDraft] = useState({}); // pool index → pasted codes
  const [codesNote, setCodesNote] = useState(null); // { i, text }

  useEffect(() => {
    let on = true;
    fetch(base).then((r) => r.json()).then((c) => { if (on) setCfg(c); }).catch(() => {});
    fetch(`${loyaltyBase}/pools`).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (on) setPools(d.pools || []); })
      .catch((e) => { if (on && e.message === '403') setPoolsDenied(true); });
    fetch(`${base}/insights`).then((r) => r.json()).then((s) => { if (on) setStats(s); }).catch(() => {});
    const suitesUrl = scope === 'my' ? '/api/my/suites' : '/api/admin/suites';
    fetch(suitesUrl).then((r) => r.json()).then((rows) => {
      const list = Array.isArray(rows) ? rows : rows.suites || [];
      // Always narrow to THIS client's events: a client user who manages more than
      // one entity would otherwise be offered another entity's events in the site
      // and per-item Event pickers, and the server drops such a tag on save.
      if (on) setSuites(list.filter((s) => s.entityId === entityId).map((s) => ({ id: s.id, name: s.name })));
    }).catch(() => {});
    return () => { on = false; };
  }, [base, loyaltyBase, scope, entityId]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setSite = (i, patch) => set({ sites: cfg.sites.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const setCat = (i, patch) => set({ catalogue: cfg.catalogue.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const setKnow = (i, patch) => set({ knowledge: cfg.knowledge.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  // Contact-form topics an entry can be tagged with (server/fanOwlContact.js);
  // null = the fanowl.contact flag is off and the tag controls stay hidden.
  const [contactOwl, setContactOwl] = useState(null);
  useEffect(() => { api.contactOwl(entityId, scope === 'my' ? 'my' : 'admin').then(setContactOwl); }, [entityId, scope]);
  const contactTopics = (contactOwl && contactOwl.topics) || [];
  // Multi-event (flag fanowl.multiEvent): one Owl serving a whole season. Items
  // carry an event tag; blank = shared by every event. Off = one flat catalogue,
  // and every event control below stays hidden.
  const multiEvent = !!cfg?.flags?.multiEvent;
  const suiteName = (id) => (suites.find((su) => su.id === id) || {}).name || '';
  const setPage = (i, pi, patch) => setSite(i, { pages: cfg.sites[i].pages.map((x, xi) => (xi === pi ? { ...x, ...patch } : x)) });
  const movePage = (i, pi, dir) => {
    const pages = [...cfg.sites[i].pages];
    const to = pi + dir;
    if (to < 0 || to >= pages.length) return;
    const [x] = pages.splice(pi, 1);
    pages.splice(to, 0, x);
    setSite(i, { pages });
  };

  // Upload catalogue images: downscale in the browser (≤1600px JPEG), POST the
  // data-URL, and drop the returned hosted URL into the item's images like any
  // pasted URL. Nothing goes live until Save.
  async function uploadImages(i, files) {
    const room = 8 - (cfg.catalogue[i].images || []).length;
    const picked = [...files].filter((f) => f.type.startsWith('image/')).slice(0, Math.max(0, room));
    if (!picked.length) { setImgNote({ i, text: room <= 0 ? 'This item already has 8 images — remove one first.' : 'Pick an image file (JPEG/PNG/WebP).' }); return; }
    setImgBusy(i); setImgNote(null);
    try {
      const urls = [];
      for (const f of picked) {
        const r = await fetch(`${base}/images`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dataUrl: await downscaleImage(f) }) });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'Upload failed — try again.');
        urls.push(d.url);
      }
      setCfg((c) => ({ ...c, catalogue: c.catalogue.map((x, j) => (j === i ? { ...x, images: [...(x.images || []), ...urls].slice(0, 8) } : x)) }));
      setImgNote({ i, text: `Uploaded ${urls.length} image${urls.length === 1 ? '' : 's'} ✓ — remember to Save.` });
    } catch (e) { setImgNote({ i, text: `⚠️ ${e.message}` }); }
    finally { setImgBusy(-1); }
  }

  // "Read the ticket site": crawl the shop → AI-suggested catalogue items merged
  // into the UNSAVED editor state (existing items never touched; dedupe by
  // label) — review prices & links, then Save. Interim until the Howler API feed.
  async function ingestCatalogue() {
    const url = ticketUrl.trim();
    if (!url) { setCatNote('Enter the ticket-shop URL first (https://…).'); return; }
    setCatIngesting(true); setCatNote('Reading the ticket site — this takes ~30–60s…');
    try {
      const r = await fetch(`${base}/ingest-catalogue`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: /^https?:\/\//i.test(url) ? url : `https://${url}`, suiteId: multiEvent ? ticketSuite : '' }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'The read failed — try again.');
      setCfg((c) => {
        // Two events legitimately both sell a "Weekend Pass", so once multi-event is
        // on the dedupe key is label + event — otherwise the second event's read
        // would come back empty and look broken.
        const key = (x) => `${String(x.label || '').toLowerCase().trim()}\u0000${multiEvent ? (x.suiteId || '') : ''}`;
        const have = new Set(c.catalogue.map(key));
        const fresh = (d.items || []).filter((x) => !have.has(key(x)));
        const skipped = (d.items || []).length - fresh.length;
        const forEvent = multiEvent ? ` for ${suiteName(ticketSuite) || 'every event'}` : '';
        setCatNote(`Read ${d.crawled.length} page${d.crawled.length === 1 ? '' : 's'} → suggested ${fresh.length} new item${fresh.length === 1 ? '' : 's'}${forEvent}${skipped ? ` (${skipped} already in the catalogue — left untouched)` : ''}. Check every price, link and image, then Save.`);
        return { ...c, catalogue: [...c.catalogue, ...fresh] };
      });
    } catch (e) { setCatNote(`⚠️ ${e.message}`); }
    finally { setCatIngesting(false); }
  }

  // "Suggest from website menu": read the site's real <nav>/<header> tabs
  // (deterministic, no AI) and load them as an UNSAVED custom button list.
  async function suggestNav(i) {
    const site = cfg.sites[i];
    if (!site.id) { setNavNote({ i, text: 'Save the site first, then suggest.' }); return; }
    const domain = (site.domains || [])[0];
    if (!domain) { setNavNote({ i, text: 'Add the site’s domain first (Sites tab) — that tells the reader which website’s menu to read.' }); return; }
    setNavBusy(true); setNavNote({ i, text: 'Reading the website’s menu…' });
    try {
      const r = await fetch(`${base}/suggest-nav`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ siteId: site.id, url: `https://${domain}` }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'The read failed — try again.');
      setCfg((c) => ({ ...c, sites: c.sites.map((x, j) => (j === i ? { ...x, navButtons: d.buttons } : x)) }));
      setNavNote({ i, text: `Found ${d.buttons.length} menu tab${d.buttons.length === 1 ? '' : 's'} — review, rename or toggle, then Save.` });
    } catch (e) { setNavNote({ i, text: `⚠️ ${e.message}` }); }
    finally { setNavBusy(false); }
  }

  // A nav button's image icon (#183): drawn onto a small canvas and sent as a
  // PNG (keeps transparency). An SVG is rasterised here too, so the hosted copy
  // is a plain image — a raw SVG served from our domain could carry script.
  async function uploadNavIcon(i, file, upd) {
    if (!file || !(file.type.startsWith('image/') || /\.svg$/i.test(file.name))) return;
    setNavBusy(true); setNavNote(null);
    try {
      const r = await fetch(`${base}/images`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dataUrl: await downscaleImage(file, 96, 0.92, 'image/png') }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Upload failed — try again.');
      upd({ iconUrl: d.url, emoji: '' });
    } catch (e) { setNavNote({ i, text: `Icon upload failed: ${e.message}` }); }
    finally { setNavBusy(false); }
  }

  // The Owl's face: one square-ish image per site, downscaled small and hosted
  // like any catalogue image; the URL rides the site's owlAvatar field.
  async function uploadAvatar(i, file) {
    if (!file || !file.type.startsWith('image/')) return;
    setAvBusy(i);
    try {
      const r = await fetch(`${base}/images`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ dataUrl: await downscaleImage(file, 512, 0.85) }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Upload failed — try again.');
      setCfg((c) => ({ ...c, sites: c.sites.map((x, j) => (j === i ? { ...x, owlAvatar: d.url } : x)) }));
    } catch (e) { setIngestNote(`⚠️ ${e.message}`); }
    finally { setAvBusy(-1); }
  }

  // `next` lets a caller save a config it just built (the Reports tab adding an
  // FAQ) without waiting for React state to catch up; plain Save passes nothing.
  async function saveCfg(next = cfg) {
    setSaving(true);
    try {
      const r = await fetch(base, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(next) });
      if (r.ok) { setCfg(await r.json()); setSavedAt(Date.now()); return true; }
      return false;
    } finally { setSaving(false); }
  }
  const save = () => saveCfg();
  const snippet = (siteKey) => `<script async src="${window.location.origin}/fan-owl.js" data-site-key="${siteKey}"></script>`;

  // "Read the website": crawl → AI suggestions merged into the UNSAVED editor
  // state (deduped; never overwriting promoter edits) — review, then Save.
  // "Write sales pitches": one AI call drafts a salesy ribbon line per page from
  // its info + ticked items; fills only EMPTY pitch fields (edits always win).
  const [pitching, setPitching] = useState(false);
  async function writePitches(siteIndex) {
    const site = cfg.sites[siteIndex];
    if (!site?.id) { setIngestNote('Save the site first, then draft pitches.'); return; }
    setPitching(true); setIngestNote('Writing pitches from each page\'s info + items…');
    try {
      const r = await fetch(`${base}/pitches`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ siteId: site.id }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Pitch drafting failed — try again.');
      const byPat = new Map((d.pitches || []).map((p) => [p.urlPattern.toLowerCase().trim(), p.pitch]));
      setCfg((c) => ({
        ...c,
        sites: c.sites.map((s, j) => (j !== siteIndex ? s : {
          ...s,
          pages: (s.pages || []).map((p) => (String(p.pitch || '').trim() ? p : { ...p, pitch: byPat.get(p.urlPattern.toLowerCase().trim()) || '' })),
        })),
      }));
      setIngestNote(`Drafted ${d.pitches.length} pitch${d.pitches.length === 1 ? '' : 'es'} — review each page's pitch line, edit freely, then Save.`);
    } catch (e) { setIngestNote(`⚠️ ${e.message}`); }
    finally { setPitching(false); }
  }

  async function ingest(siteIndex) {
    const url = ingestUrl.trim();
    if (!url) { setIngestNote('Enter the site URL first (https://…).'); return; }
    setIngesting(true); setIngestNote('Reading the site — this takes ~30–60s…');
    try {
      const r = await fetch(`${base}/ingest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: /^https?:\/\//i.test(url) ? url : `https://${url}`, suiteId: multiEvent ? ingestSuite : '' }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'The crawl failed — try again.');
      setCfg((c) => {
        // Two events legitimately answer "what time do gates open?" differently, so
        // once multi-event is on the dedupe key carries the event too.
        const kKey = (k) => `${(k.question || k.body || '').toLowerCase().trim()}\u0000${multiEvent ? (k.suiteId || '') : ''}`;
        const haveQ = new Set(c.knowledge.map(kKey));
        const newKnow = (d.knowledge || []).filter((k) => !haveQ.has(kKey(k)));
        const sites = c.sites.map((s, j) => {
          if (j !== siteIndex) return s;
          const byPat = new Map((d.pages || []).map((p) => [p.urlPattern.toLowerCase().trim(), p]));
          const merged = (s.pages || []).map((p) => {
            const sug = byPat.get(p.urlPattern.toLowerCase().trim());
            if (sug) byPat.delete(p.urlPattern.toLowerCase().trim());
            if (!sug) return p;
            return {
              ...p,
              content: String(p.content || '').trim() ? p.content : (sug.content || ''),
              note: p.note || sug.note || '',
              starters: (p.starters || []).length ? p.starters : (sug.starters || []),
            };
          });
          const newPages = [...byPat.values()].map((p) => ({ ...p, itemIds: [] }));
          return { ...s, pages: [...merged, ...newPages] };
        });
        const forEvent = multiEvent ? ` for ${suiteName(ingestSuite) || 'every event'}` : '';
        const via = d.via === 'sitemap' ? ' (found via the site’s sitemap — its menu isn’t crawlable)' : '';
        const nothing = !newKnow.length && !(d.pages || []).length ? ' The pages were readable but the Owl found nothing event-specific to suggest — try pointing it at a specific page (FAQ, line-up, tickets).' : '';
        setIngestNote(`Read ${d.crawled.length} page${d.crawled.length === 1 ? '' : 's'}${via} → suggested ${newKnow.length} FAQ entries + ${(d.pages || []).length} pages (info + chips)${forEvent}.${nothing || ' Review, edit freely, then Save.'}`);
        return { ...c, sites, knowledge: [...c.knowledge, ...newKnow] };
      });
    } catch (e) { setIngestNote(`⚠️ ${e.message}`); }
    finally { setIngesting(false); }
  }

  if (!cfg) return <p style={small}>Loading the Fan Owl config…</p>;

  const saveBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
      <button type="button" style={primaryBtn} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save Fan Owl config'}</button>
      {savedAt > 0 && Date.now() - savedAt < 4000 && <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Saved ✓</span>}
    </div>
  );

  return (
    <div style={{ marginTop: 14 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="owl" size={16} />Fan Owl — website booking guide</h2>
      <p style={small}>The Owl on the event's public website: it guides fans to the right ticket, answers only from what you approve here, and hands out the buy links you supply. Nothing here is private — only publish what any fan may see.</p>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid var(--hairline)', padding: '10px 0', marginBottom: 4 }}>
        {TABS.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            style={{ ...btn, fontWeight: tab === key ? 700 : 500, background: tab === key ? 'var(--text)' : 'transparent', color: tab === key ? 'var(--bg, #fff)' : 'var(--text)', border: tab === key ? 0 : btn.border }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'sites' && (
        <>
          <p style={small}>One site per website. Paste the embed snippet once, site-wide. Domains lock which websites may use the key (leave empty while testing).</p>
          {cfg.sites.map((s, i) => (
            <div key={s.id || i} style={card}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
                <div>
                  <div style={small}>Site name</div>
                  <input style={input} value={s.name} onChange={(e) => setSite(i, { name: e.target.value })} placeholder="e.g. Retreat Yourself website" />
                </div>
                <div>
                  <div style={small}>Event</div>
                  <select style={input} value={s.suiteId || ''} onChange={(e) => setSite(i, { suiteId: e.target.value })}>
                    <option value="">— whole client —</option>
                    {suites.map((su) => <option key={su.id} value={su.id}>{su.name}</option>)}
                  </select>
                </div>
                <div>
                  <div style={small}>Allowed domains (comma-separated, e.g. ry.howler.co.za)</div>
                  <CommaList style={input} value={s.domains} onChange={(domains) => setSite(i, { domains })} />
                </div>
                <div>
                  <div style={small}>Teaser (the ribbon line when no page mapping matches)</div>
                  <input style={input} value={s.teaser} onChange={(e) => setSite(i, { teaser: e.target.value })} placeholder="e.g. Tickets are live — ask me anything" />
                </div>
                <div>
                  <div style={small}>Ask box message — the grey prompt inside the question box, on the website and in the chat (blank = a page-aware default like “Ask about tickets…”)</div>
                  <input style={input} value={s.askPlaceholder || ''} maxLength={80} onChange={(e) => setSite(i, { askPlaceholder: e.target.value })} placeholder="e.g. Ask me about tickets, or anything else" />
                </div>
                <div>
                  <div style={small}>Brand colour (hex — blank adopts your Pulse branding; also editable under 🪄 Personality)</div>
                  <input style={input} value={s.brandColor} onChange={(e) => setSite(i, { brandColor: e.target.value })} placeholder="#111111" />
                </div>
                <div>
                  <div style={small}>Daily chat budget (messages/day; over it the widget degrades to ribbon-only)</div>
                  <input style={input} type="number" value={s.dailyBudget} onChange={(e) => setSite(i, { dailyBudget: e.target.value })} />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '10px 0 4px' }}>
                <input type="checkbox" checked={!!s.enabled} onChange={(e) => setSite(i, { enabled: e.target.checked })} style={{ width: 16, height: 16 }} />
                Enabled on the website (fans can see the widget) — WhatsApp replies have their own switch under the WhatsApp tab, so one can be on while the other is off
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '0 0 10px' }}>
                <input type="checkbox" checked={!!s.allowApp} onChange={(e) => setSite(i, { allowApp: e.target.checked })} style={{ width: 16, height: 16 }} />
                Allow in Howler app (the super app can open this Owl — app boots skip the domain allowlist)
              </label>
              {/* Live support: only offer it where someone is actually watching Conversations. */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, margin: '0 0 10px' }}>
                <input type="checkbox" checked={!!s.humanHandoff} onChange={(e) => setSite(i, { humanHandoff: e.target.checked })} style={{ width: 16, height: 16, marginTop: 2 }} />
                <span>
                  Let fans talk to a person — a &ldquo;Talk to a person&rdquo; button, and the conversation opens in Conversations where your team replies LIVE on the fan&rsquo;s page (or by email if they have gone).
                  <br />
                  <span style={{ color: 'var(--muted)', fontSize: 12 }}>Leave it off unless someone is watching: the Owl then says plainly it can&rsquo;t put them through, and keeps helping.</span>
                </span>
              </label>
              {s.humanHandoff && (
                <div style={{ margin: '0 0 10px 24px', display: 'grid', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13 }}>
                    <input type="checkbox" checked={s.handoffVerify !== false} onChange={(e) => setSite(i, { handoffVerify: e.target.checked })} style={{ width: 16, height: 16, marginTop: 2 }} />
                    <span>
                      Verify them first — the Owl asks for an email and checks a 6-digit code before bringing anyone in.
                      <br />
                      <span style={{ color: 'var(--muted)', fontSize: 12 }}>This is what lets you answer someone who has closed the tab, and it saves the fan to your records. A fan who refuses is still put through, live only.</span>
                    </span>
                  </label>
                  <div>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>When is someone watching?</div>
                    <input style={input} value={s.staffedHours || ''} onChange={(e) => setSite(i, { staffedHours: e.target.value })} placeholder="e.g. Mon-Fri 09:00-17:00; Sat 10:00-14:00 — blank means always" />
                    <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>Your own timezone. One shift per line or separated by a semicolon, so different days can have different hours (<code>Mon-Thu 09:00-17:00; Fri 09:00-14:00; Sat 10:00-13:00</code>). Anyone on your team with Pulse open counts as on duty whatever the hours say. Outside them the Owl still takes the conversation — it says the team is offline, asks what they need and where to reach them, and saves it here.</div>
                  </div>
                </div>
              )}
              {s.siteKey && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <code style={{ fontSize: 11, background: 'var(--hairline)', padding: '6px 8px', borderRadius: 6, overflowX: 'auto', maxWidth: '100%' }}>{snippet(s.siteKey)}</code>
                  <button type="button" style={btn} onClick={() => navigator.clipboard?.writeText(snippet(s.siteKey))}>Copy snippet</button>
                  <a href={`/fan-owl-test?k=${s.siteKey}`} target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', fontWeight: 700 }}>▶ Preview</a>
                </div>
              )}

              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <button type="button" style={{ ...btn, color: 'var(--danger, #b3261e)' }} onClick={() => set({ sites: cfg.sites.filter((_, j) => j !== i) })}>Delete site</button>
              </div>
            </div>
          ))}
          <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => set({ sites: [...cfg.sites, { name: '', suiteId: '', domains: [], enabled: false, allowApp: false, humanHandoff: false, handoffVerify: true, staffedHours: '', teaser: '', brandColor: '', dailyBudget: 400, owlName: '', owlAvatar: '', owlIntro: '', persona: '', guardrails: '', defaultLang: '', widgetTheme: '', widgetStyle: '', heroHome: false, navStyle: '', navButtons: null, pages: [] }] })}>+ Add site</button>
          {saveBar}
        </>
      )}

      {tab === 'persona' && (
        <>
          <p style={small}>Make each site's Owl your own: its face, name, voice and house rules. Personality shapes HOW it speaks — the hard rules (real prices only, nothing invented, no fake urgency) always win. 💡 Special tips live under FAQs as the “tip” kind — the Owl volunteers them when they genuinely help.</p>
          {!cfg.sites.length && <p style={small}>Add a site first (Sites section) — the personality belongs to a site.</p>}
          {cfg.sites.map((s, i) => (
            <div key={s.id || i} style={card}>
              {cfg.sites.length > 1 && <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8 }}>{s.name || 'Untitled site'}</div>}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                {s.owlAvatar
                  ? <img src={s.owlAvatar} alt="" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                  : <img src="/fan-owl-mark.png" alt="" style={{ width: 56, height: 56, borderRadius: '50%' }} />}
                <label style={{ ...btn, display: 'inline-flex', alignItems: 'center', fontWeight: 700, opacity: avBusy === i ? 0.6 : 1 }}>
                  {avBusy === i ? 'Uploading…' : (s.owlAvatar ? 'Change face' : '📷 Upload a face')}
                  <input type="file" accept="image/*" style={{ display: 'none' }} disabled={avBusy !== -1}
                    onChange={(e) => { uploadAvatar(i, e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                {s.owlAvatar && <button type="button" style={btn} onClick={() => setSite(i, { owlAvatar: '' })}>Back to the Howler owl</button>}
                <span style={{ ...small, margin: 0 }}>Shows on the launcher button, the welcome and the chat header. Blank = the Howler owl mark (the same owl as the dashboard tiles).</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: 8 }}>
                <div>
                  <div style={small}>Owl's name (the chat header title)</div>
                  <input style={input} value={s.owlName || ''} maxLength={40} placeholder="e.g. Kappa Guide" onChange={(e) => setSite(i, { owlName: e.target.value })} />
                </div>
                <div>
                  <div style={small}>Intro line (the Owl's hello — the hero's headline on the home page; on the ask bar and the launcher it shows once per visit as a welcome strip / first bubble)</div>
                  <input style={input} value={s.owlIntro || ''} maxLength={200} placeholder="e.g. Ciao! I'm your festival insider — ask me anything 🎶" onChange={(e) => setSite(i, { owlIntro: e.target.value })} />
                </div>
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={small}>Default language — the Owl greets in the fan's phone/browser language when it can tell; this is the fallback (it always answers in whatever language the fan writes)</div>
                <select style={input} value={s.defaultLang || ''} onChange={(e) => setSite(i, { defaultLang: e.target.value })}>
                  {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginTop: 8 }}>
                <div>
                  <div style={small}>Widget colour — launcher, header & buttons (hex)</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span aria-hidden style={{ width: 26, height: 26, borderRadius: 8, flex: '0 0 auto', border: '1px solid var(--hairline)', background: s.brandColor || cfg.inherited?.brandColor || '#111' }} />
                    <input style={input} value={s.brandColor || ''} placeholder={cfg.inherited?.brandColor || '#111111'} onChange={(e) => setSite(i, { brandColor: e.target.value })} />
                  </div>
                  <div style={small}>Blank = adopts your Pulse brand{cfg.inherited?.brandColor ? ` (${cfg.inherited.brandColor})` : ''} — set one here if that clashes with this website.</div>
                </div>
                <div>
                  <div style={small}>Widget theme — the chat surface</div>
                  <select style={input} value={s.widgetTheme || ''} onChange={(e) => setSite(i, { widgetTheme: e.target.value })}>
                    <option value="">Auto — follow the fan's device</option>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </div>
                <div>
                  <div style={small}>Widget style — how the Owl sits on the website</div>
                  <select style={input} value={s.widgetStyle || ''} onChange={(e) => setSite(i, { widgetStyle: e.target.value })}>
                    <option value="">Floating launcher (default)</option>
                    <option value="bar">Persistent ask bar — always-on input + nav at the bottom of every page</option>
                  </select>
                  {s.widgetStyle === 'bar' && (
                    <select style={{ ...input, marginTop: 8 }} value={s.barLook || ''} onChange={(e) => setSite(i, { barLook: e.target.value })} aria-label="Bar look">
                      <option value="">Bar look: Solid — a white (or dark) card that stands on the page</option>
                      <option value="glass">Bar look: Glass — translucent, the page shows through; no border on the ask box, softer text (follows the theme above)</option>
                    </select>
                  )}
                  {s.widgetStyle === 'bar' && s.barLook === 'glass' && (
                    <label style={{ display: 'block', marginTop: 8 }}>
                      <div style={small}>Glass clarity — how far the page shows through: <b>{Number(s.barGlass) || 0}</b> (0 = frosted, the default · 100 = almost clear)</div>
                      <input type="range" min={0} max={100} step={5} value={Number(s.barGlass) || 0} onChange={(e) => setSite(i, { barGlass: Number(e.target.value) })} style={{ width: '100%', maxWidth: 420 }} aria-label="Glass clarity" />
                    </label>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginTop: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!s.heroHome} onChange={(e) => setSite(i, { heroHome: e.target.checked })} style={{ width: 16, height: 16 }} />
                    Hero chat on the home page — greet fans with the ask box centred (folds away on scroll or dismiss)
                  </label>
                </div>
              </div>
              <div style={{ ...small, marginTop: 8 }}>Personality & voice — how should it sound? (style only; it can never change prices or facts)</div>
              <textarea style={{ ...input, resize: 'vertical' }} rows={3} value={s.persona || ''} maxLength={2000}
                placeholder="e.g. Warm and cheeky, proudly local, first-name basis, loves music puns, answers in the fan's language, keeps it short."
                onChange={(e) => setSite(i, { persona: e.target.value })} />
              <div style={{ ...small, marginTop: 8 }}>Dos & don'ts — house rules for this Owl</div>
              <textarea style={{ ...input, resize: 'vertical' }} rows={3} value={s.guardrails || ''} maxLength={2000}
                placeholder="e.g. Always mention the waiting list when something's sold out. Don't recommend camping to families. Never discuss other festivals."
                onChange={(e) => setSite(i, { guardrails: e.target.value })} />
              {s.siteKey && (
                <div style={{ marginTop: 10 }}>
                  <a href={`/fan-owl-test?k=${s.siteKey}`} target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', fontWeight: 700 }}>▶ Preview this personality</a>
                  <span style={{ ...small, marginLeft: 8 }}>Save first — the preview reads the saved config.</span>
                </div>
              )}
            </div>
          ))}
          {cfg.sites.length > 0 && saveBar}
        </>
      )}

      {tab === 'nav' && (
        <>
          <p style={small}>The quick buttons fans use to hop around your site from the Owl — tap = the Owl takes them there and follows with that page's context. Buttons come from your 📄 Pages automatically; curate them per site below.</p>
          {!cfg.sites.length && <p style={small}>Add a site first (Sites section) — the buttons belong to a site.</p>}
          {cfg.sites.map((s, i) => (
            <div key={s.id || i} style={card}>
              {cfg.sites.length > 1 && <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8 }}>{s.name || 'Untitled site'}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={small}>Where they live</div>
                    <select style={input} value={s.navStyle || ''} onChange={(e) => setSite(i, { navStyle: e.target.value })}>
                      <option value="">Icon strip under the header (default)</option>
                      <option value="plus">＋ menu next to the message box</option>
                      <option value="pills">Labelled pills above the message box (centred)</option>
                      <option value="below">Labelled pills below the message box (centred)</option>
                      <option value="off">Off — chat only</option>
                    </select>
                  </div>
                  <div>
                    <div style={small}>Which buttons</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" style={{ ...btn, fontWeight: 700, background: !Array.isArray(s.navButtons) ? 'var(--text)' : 'transparent', color: !Array.isArray(s.navButtons) ? 'var(--bg, #fff)' : 'var(--text)', border: !Array.isArray(s.navButtons) ? 0 : btn.border }}
                        onClick={() => setSite(i, { navButtons: null })}>Auto — from your pages</button>
                      <button type="button" style={{ ...btn, fontWeight: 700, background: Array.isArray(s.navButtons) ? 'var(--text)' : 'transparent', color: Array.isArray(s.navButtons) ? 'var(--bg, #fff)' : 'var(--text)', border: Array.isArray(s.navButtons) ? 0 : btn.border }}
                        onClick={() => { if (!Array.isArray(s.navButtons)) setSite(i, { navButtons: (s.pages || []).filter((p) => navigablePath(p.urlPattern)).slice(0, 12).map((p) => ({ kind: 'page', urlPattern: p.urlPattern, label: '', emoji: '', enabled: true })) }); }}>Custom</button>
                      <button type="button" style={{ ...btn, borderStyle: 'dashed' }} disabled={navBusy} onClick={() => suggestNav(i)}>{navBusy ? 'Reading…' : '🔮 Suggest from website menu'}</button>
                    </div>
                  </div>
                </div>
                {!Array.isArray(s.navButtons) && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
                    {(s.pages || []).filter((p) => navigablePath(p.urlPattern)).slice(0, 8).map((p) => (
                      <span key={p.urlPattern} style={{ fontSize: 12, border: '1px solid var(--hairline)', borderRadius: 999, padding: '5px 11px' }}>{NAV_TYPE_LABELS[p.pageType] || NAV_TYPE_LABELS.other} <span style={{ color: 'var(--muted)' }}>· {navigablePath(p.urlPattern)}</span></span>
                    ))}
                    {!(s.pages || []).some((p) => navigablePath(p.urlPattern)) && <span style={small}>No mapped pages yet — add them under 📄 Pages (or run "Read the website") and buttons appear automatically.</span>}
                    <span style={small}>Switch to Custom to rename, reorder, toggle or add your own links.</span>
                  </div>
                )}
                {Array.isArray(s.navButtons) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                    {s.navButtons.map((b, bi) => {
                      const pg = b.kind !== 'custom' ? (s.pages || []).find((p) => p.urlPattern === b.urlPattern) : null;
                      const defLabel = b.kind === 'custom' ? 'Custom link' : (NAV_TYPE_LABELS[pg?.pageType] || NAV_TYPE_LABELS.other);
                      const upd = (patch) => setSite(i, { navButtons: s.navButtons.map((x, xi) => (xi === bi ? { ...x, ...patch } : x)) });
                      const move = (dir) => {
                        const arr = [...s.navButtons]; const to = bi + dir;
                        if (to < 0 || to >= arr.length) return;
                        const [x] = arr.splice(bi, 1); arr.splice(to, 0, x);
                        setSite(i, { navButtons: arr });
                      };
                      return (
                        <div key={`${b.kind}-${b.urlPattern || b.path}-${bi}`} style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--hairline)', borderRadius: 10, padding: '6px 8px', flexWrap: 'wrap', opacity: b.enabled === false ? 0.55 : 1 }}>
                        <input type="checkbox" checked={b.enabled !== false} onChange={(e) => upd({ enabled: e.target.checked })} style={{ width: 16, height: 16 }} title="Show this button" />
                        {b.iconUrl
                          ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <img src={b.iconUrl} alt="" style={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--hairline)' }} />
                              <button type="button" aria-label="Remove image icon" title="Remove the image icon" style={{ ...btn, minHeight: 28, padding: '2px 8px', fontSize: 11 }} onClick={() => upd({ iconUrl: '' })}>✕</button>
                            </span>
                          )
                          : (<>
                            <input style={{ ...input, width: 54, textAlign: 'center', padding: '7px 4px' }} value={b.emoji || ''} maxLength={4} placeholder="icon" title="Emoji icon (blank = the page-type icon; none for a custom link)" onChange={(e) => upd({ emoji: e.target.value })} />
                            <label title="Upload an image or SVG icon" style={{ ...btn, minHeight: 28, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>
                              Upload icon
                              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg" style={{ display: 'none' }}
                                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; uploadNavIcon(i, f, upd); }} />
                            </label>
                          </>)}
                        <input style={{ ...input, flex: '1 1 110px', width: 'auto' }} value={b.label || ''} maxLength={24} placeholder={defLabel} title="Button label" onChange={(e) => upd({ label: e.target.value })} />
                        {b.kind === 'custom'
                          ? <input style={{ ...input, flex: '2 1 140px', width: 'auto' }} value={b.path || ''} placeholder="/glamping (path on your site)" onChange={(e) => upd({ path: e.target.value })} />
                          : <span style={{ ...small, margin: 0, flex: '2 1 140px', fontFamily: 'ui-monospace, monospace' }}>{navigablePath(b.urlPattern)} · {pg ? pg.pageType : '⚠️ page mapping removed'}</span>}
                        <button type="button" aria-label="Move up" disabled={bi === 0} style={{ ...btn, minHeight: 28, padding: '2px 8px', fontSize: 11, opacity: bi === 0 ? 0.35 : 1 }} onClick={() => move(-1)}>▲</button>
                        <button type="button" aria-label="Move down" disabled={bi === s.navButtons.length - 1} style={{ ...btn, minHeight: 28, padding: '2px 8px', fontSize: 11, opacity: bi === s.navButtons.length - 1 ? 0.35 : 1 }} onClick={() => move(1)}>▼</button>
                        <button type="button" aria-label="Remove" style={{ ...btn, minHeight: 28, padding: '2px 8px', fontSize: 11, color: 'var(--danger, #b3261e)' }} onClick={() => setSite(i, { navButtons: s.navButtons.filter((_, xi) => xi !== bi) })}>✕</button>
                        </div>
                      );
                    })}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" style={btn} onClick={() => setSite(i, { navButtons: [...s.navButtons, { kind: 'custom', urlPattern: '', label: '', emoji: '🔗', path: '', enabled: true }] })}>+ Add custom link</button>
                      <button type="button" style={btn} onClick={() => setSite(i, { navButtons: (s.pages || []).filter((p) => navigablePath(p.urlPattern)).slice(0, 12).map((p) => ({ kind: 'page', urlPattern: p.urlPattern, label: '', emoji: '', enabled: true })) })}>↺ Rebuild from pages</button>
                      <span style={{ ...small, alignSelf: 'center' }}>Up to 8 show; blank label/icon = the page-type default.</span>
                    </div>
                  </div>
                )}
                {navNote && navNote.i === i && <p style={{ ...small, marginTop: 6 }}>{navNote.text}</p>}
              {s.siteKey && (
                <div style={{ marginTop: 10 }}>
                  <a href={`/fan-owl-test?k=${s.siteKey}`} target="_blank" rel="noreferrer" style={{ ...btn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', fontWeight: 700 }}>▶ Preview</a>
                  <span style={{ ...small, marginLeft: 8 }}>Save first — the preview reads the saved config.</span>
                </div>
              )}
            </div>
          ))}
          {cfg.sites.length > 0 && saveBar}
        </>
      )}

      {tab === 'pages' && (
        <>
          <p style={small}>What the Owl knows & sells on each page. When the page URL contains the pattern (* = wildcard), the Owl leads with the ticked items, answers from that page's info, and shows that page's chips. Longest match wins; unmatched pages get the whole catalogue. Page info is searchable from anywhere.{multiEvent ? ' Tagging a page with an event tells the Owl which event a fan on those URLs is asking about — it then answers only from that event’s pages and offers only that event’s tickets. Leave site-wide pages (home, FAQs, venue) on “every event”.' : ''}</p>
          {!cfg.sites.length && <p style={small}>Add a site first (Sites section) — pages belong to a site.</p>}
          {cfg.sites.map((s, i) => (
            <div key={s.id || i} style={card}>
              {cfg.sites.length > 1 && <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4 }}>{s.name || 'Untitled site'}</div>}

              <details style={{ marginBottom: 6 }}>
                <summary style={summaryStyle}>🔮 Read the website — draft pages, info & chips automatically</summary>
                <p style={small}>Point the Owl at the event site — it reads the pages and SUGGESTS page info, chips and FAQs. Nothing goes live until you review and Save.</p>
                {multiEvent && (
                  <>
                    <div style={small}>Which event does this part of the site belong to? Read one section per event — each run tags the pages it suggests.</div>
                    <select style={{ ...input, marginBottom: 8 }} value={ingestSuite} onChange={(e) => setIngestSuite(e.target.value)}>
                      <option value="">— every event (site-wide pages) —</option>
                      {suites.map((su) => <option key={su.id} value={su.id}>{su.name}</option>)}
                    </select>
                  </>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input style={{ ...input, flex: '1 1 220px', width: 'auto' }} value={ingestUrl} placeholder={`https://${(s.domains || [])[0] || 'your-event-site.com'}`} onChange={(e) => setIngestUrl(e.target.value)} />
                  <button type="button" style={{ ...btn, fontWeight: 700 }} disabled={ingesting} onClick={() => ingest(i)}>{ingesting ? 'Reading…' : 'Read & suggest'}</button>
                </div>
                {ingestNote && <p style={{ ...small, marginTop: 6 }}>{ingestNote}</p>}
                <p style={small}>Want this kept up to date without re-reading by hand? <button type="button" style={linkBtn} onClick={() => setTab('sync')}>Set up Auto-update</button></p>
              </details>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '2px 0 6px' }}>
                <button type="button" style={{ ...btn, fontWeight: 700 }} disabled={pitching} onClick={() => writePitches(i)}>{pitching ? 'Writing…' : '✨ Write sales pitches'}</button>
                <span style={small}>Drafts the salesy ribbon line per page (fills empty pitch fields only).</span>
              </div>

              {(s.pages || []).map((p, pi) => (
                <details key={p.id || pi} style={{ borderTop: '1px dashed var(--hairline)' }} open={!p.urlPattern}>
                  <summary style={{ ...summaryStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {p.urlPattern || 'New page'} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· {p.pageType}{multiEvent ? ` · ${suiteName(p.suiteId) || 'every event'}` : ''}{(p.itemIds || []).length ? ` · ${p.itemIds.length} item${p.itemIds.length === 1 ? '' : 's'}` : ''}{p.content ? ' · info ✓' : ''}{(p.starters || []).length ? ' · chips ✓' : ''}</span>
                    </span>
                    <button type="button" aria-label="Move up" disabled={pi === 0} style={{ ...btn, minHeight: 28, padding: '2px 9px', fontSize: 11, opacity: pi === 0 ? 0.35 : 1 }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); movePage(i, pi, -1); }}>▲</button>
                    <button type="button" aria-label="Move down" disabled={pi === (s.pages || []).length - 1} style={{ ...btn, minHeight: 28, padding: '2px 9px', fontSize: 11, opacity: pi === (s.pages || []).length - 1 ? 0.35 : 1 }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); movePage(i, pi, 1); }}>▼</button>
                  </summary>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr auto', gap: 8 }}>
                    <input style={input} value={p.urlPattern} placeholder="/accommodation or /artists/*" onChange={(e) => setPage(i, pi, { urlPattern: e.target.value })} />
                    <select style={input} value={p.pageType} onChange={(e) => setPage(i, pi, { pageType: e.target.value })}>
                      {PAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button type="button" style={btn} onClick={() => setSite(i, { pages: s.pages.filter((_, xi) => xi !== pi) })}>Remove</button>
                  </div>
                  {multiEvent && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr', gap: 8, marginTop: 6, alignItems: 'center' }}>
                      <div style={{ ...small, margin: 0 }}>Event</div>
                      <select style={input} value={p.suiteId || ''} onChange={(e) => setPage(i, pi, { suiteId: e.target.value })}>
                        <option value="">— every event (site-wide: FAQs, home, venue…) —</option>
                        {suites.map((su) => <option key={su.id} value={su.id}>{su.name}</option>)}
                      </select>
                    </div>
                  )}
                  <input style={{ ...input, marginTop: 6 }} value={p.note} placeholder="One-liner: what is this page? (e.g. Luna X plays Saturday night)" onChange={(e) => setPage(i, pi, { note: e.target.value })} />
                  <textarea style={{ ...input, marginTop: 6, resize: 'vertical' }} rows={3} value={p.content || ''}
                    placeholder="Page info — everything the Owl may tell fans about this page's topic (e.g. all the accommodation options). Filled by “Read the website”; add or edit freely."
                    onChange={(e) => setPage(i, pi, { content: e.target.value })} />
                  <CommaList style={{ ...input, marginTop: 6 }} value={p.starters} max={4}
                    placeholder="Suggested chips for this page, comma-separated (e.g. What are the glamping options?, Is bedding included?)"
                    onChange={(starters) => setPage(i, pi, { starters })} />
                  <input style={{ ...input, marginTop: 6 }} value={p.pitch || ''} maxLength={160}
                    placeholder="Sales pitch — the salesy teaser line fans see on this page (✨ drafts it; edit freely, e.g. Glamping pods from R1,500 — wake up at the festival 🌙)"
                    onChange={(e) => setPage(i, pi, { pitch: e.target.value })} />
                  <div style={{ ...small, marginTop: 6 }}>Lead with these catalogue items on this page:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingBottom: 8 }}>
                    {cfg.catalogue.map((c) => {
                      const on = (p.itemIds || []).includes(c.id);
                      return (
                        <button key={c.id} type="button"
                          style={{ ...btn, minHeight: 32, padding: '5px 10px', fontSize: 12, background: on ? 'var(--text)' : 'transparent', color: on ? 'var(--bg, #fff)' : 'var(--text)' }}
                          onClick={() => setPage(i, pi, { itemIds: on ? p.itemIds.filter((id) => id !== c.id) : [...(p.itemIds || []), c.id] })}>
                          {c.label}
                        </button>
                      );
                    })}
                    {!cfg.catalogue.length && <span style={small}>No catalogue items yet — add them in the Catalogue section.</span>}
                  </div>
                </details>
              ))}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" style={btn} onClick={() => setSite(i, { pages: [...(s.pages || []), { urlPattern: '', pageType: 'other', suiteId: multiEvent ? ingestSuite : '', itemIds: [], note: '', content: '', starters: [] }] })}>+ Page</button>
                <button type="button" style={btn} title="Seed one mapping per Howler-app screen (app:// patterns) — give each its own info, chips and pitch, exactly like website pages"
                  onClick={() => {
                    const have = new Set((s.pages || []).map((p) => String(p.urlPattern).trim().toLowerCase()));
                    const add = APP_SCREENS.filter((p) => !have.has(p.urlPattern)).map((p) => ({ ...p, itemIds: [], content: '', starters: [] }));
                    if (add.length) setSite(i, { pages: [...(s.pages || []), ...add] });
                  }}>📱 + Howler app screens</button>
                <span style={small}>App screens are context-only mappings (<code>app://…</code>) — they tune the Owl per app screen (incl. app-only ones like My&nbsp;Tickets, feed, chat) and never become website nav buttons.</span>
              </div>
            </div>
          ))}
          {cfg.sites.length > 0 && saveBar}
        </>
      )}

      {tab === 'catalogue' && (
        <>
          <p style={small}>Tickets, add-ons & bundles — the Owl's ONLY price/product facts, and the only links it can hand out. Paste the Howler checkout link per item (Pulse adds tracking automatically). Images show as a scrollable strip on the offer card.{multiEvent ? ' This client runs several events off one Owl: tag each item with the event it belongs to — the Owl only ever offers (and only ever links) that event’s items plus the ones left on “every event”, like merch or parking.' : ''}</p>
          <details style={{ marginBottom: 6 }}>
            <summary style={summaryStyle}>🔮 Read the ticket site — draft the catalogue automatically</summary>
            <p style={small}>Point the Owl at the event's ticket shop (e.g. the Howler event page) — it reads the tickets, prices, buy links and images and SUGGESTS catalogue items. Existing items are never touched; nothing goes live until you review and Save. (Interim tool — this will pull straight from Howler via API later.)</p>
            {multiEvent && (
              <>
                <div style={small}>Which event are these tickets for? Read one ticket link per event — each run tags what it finds.</div>
                <select style={{ ...input, marginBottom: 8 }} value={ticketSuite} onChange={(e) => setTicketSuite(e.target.value)}>
                  <option value="">— every event (shared items) —</option>
                  {suites.map((su) => <option key={su.id} value={su.id}>{su.name}</option>)}
                </select>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input style={{ ...input, flex: '1 1 220px', width: 'auto' }} value={ticketUrl} placeholder="https://howler.co.za/events/…" onChange={(e) => setTicketUrl(e.target.value)} />
              <button type="button" style={{ ...btn, fontWeight: 700 }} disabled={catIngesting} onClick={ingestCatalogue}>{catIngesting ? 'Reading…' : 'Read & suggest'}</button>
            </div>
            {catNote && <p style={{ ...small, marginTop: 6 }}>{catNote}</p>}
            <p style={small}>Prices and availability change — <button type="button" style={linkBtn} onClick={() => setTab('sync')}>set up Auto-update</button> to re-read the shop on a schedule.</p>
          </details>
          {(() => {
            const renderItem = (c, i) => (
              <details key={c.id || i} style={{ ...card, paddingTop: 4, paddingBottom: 8 }} open={!c.label}>
                <summary style={summaryStyle}>
                  {c.label || 'New item'}{' '}
                  <span style={{ fontWeight: 400, color: 'var(--muted)' }}>
                    · {c.kind}{c.price ? ` · ${c.currency} ${c.price}` : ''}{c.availability ? ` · ${c.availability}` : ''}{(c.images || []).length ? ' · 📷' : ''}{c.public === false ? ' · hidden' : ''}
                  </span>
                </summary>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1fr 1fr 1fr 1fr', gap: 8 }}>
                  <input style={input} value={c.label} placeholder="Label (e.g. Weekend Pass)" onChange={(e) => setCat(i, { label: e.target.value })} />
                  <select style={input} value={c.kind} onChange={(e) => setCat(i, { kind: e.target.value })}>
                    {ITEM_KINDS.map((k) => <option key={k} value={k}>{k === 'addon' ? 'add-on' : k}</option>)}
                  </select>
                  <input style={input} value={c.price} placeholder="Price (e.g. 950)" onChange={(e) => setCat(i, { price: e.target.value })} />
                  <input style={input} value={c.currency} placeholder="ZAR" onChange={(e) => setCat(i, { currency: e.target.value })} />
                  <select style={input} value={c.availability} onChange={(e) => setCat(i, { availability: e.target.value })}>
                    {AVAILABILITY.map((a) => <option key={a} value={a}>{a || '— availability —'}</option>)}
                  </select>
                </div>
                {multiEvent && (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr', gap: 8, marginTop: 6, alignItems: 'center' }}>
                    <div style={{ ...small, margin: 0 }}>Event</div>
                    <select style={input} value={c.suiteId || ''} onChange={(e) => setCat(i, { suiteId: e.target.value })}>
                      <option value="">— every event (shared: merch, parking, shuttles…) —</option>
                      {suites.map((su) => <option key={su.id} value={su.id}>{su.name}</option>)}
                    </select>
                  </div>
                )}
                <input style={{ ...input, marginTop: 6 }} value={c.deepLink} placeholder="Howler checkout link (https://…)" onChange={(e) => setCat(i, { deepLink: e.target.value })} />
                <input style={{ ...input, marginTop: 6 }} value={c.description} placeholder="One-liner the Owl can use (what's included, who it's for)" onChange={(e) => setCat(i, { description: e.target.value })} />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                  <label style={{ ...btn, display: 'inline-flex', alignItems: 'center', fontWeight: 700, opacity: imgBusy === i ? 0.6 : 1 }}>
                    {imgBusy === i ? 'Uploading…' : '📷 Upload images'}
                    <input type="file" accept="image/*" multiple style={{ display: 'none' }} disabled={imgBusy !== -1}
                      onChange={(e) => { uploadImages(i, e.target.files); e.target.value = ''; }} />
                  </label>
                  <span style={{ ...small, margin: 0 }}>Up to 8 — fans scroll through them on the offer card.</span>
                </div>
                {imgNote && imgNote.i === i && <p style={{ ...small, marginTop: 6 }}>{imgNote.text}</p>}
                {(c.images || []).filter((u) => /^https?:\/\//i.test(u)).length > 0 && (
                  <div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginTop: 6, paddingTop: 8 }}>
                    {c.images.filter((u) => /^https?:\/\//i.test(u)).map((u) => (
                      <div key={u} style={{ position: 'relative', flex: '0 0 auto' }}>
                        <img src={u} alt="" style={{ height: 54, borderRadius: 8, objectFit: 'cover', display: 'block' }} />
                        <button type="button" aria-label="Remove image" title="Remove image"
                          onClick={() => setCat(i, { images: c.images.filter((x) => x !== u) })}
                          style={{ position: 'absolute', top: -8, right: -8, width: 24, height: 24, borderRadius: 12, border: 0, background: 'var(--text)', color: 'var(--bg, #fff)', fontSize: 12, lineHeight: '24px', padding: 0, cursor: 'pointer' }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                <details style={{ marginTop: 6 }}>
                  <summary style={{ ...small, cursor: 'pointer', listStyle: 'none' }}>Paste image URLs instead…</summary>
                  <CommaList style={{ ...input, marginTop: 4 }} value={c.images} max={8}
                    placeholder="Image URLs, comma-separated (https://…)"
                    onChange={(images) => setCat(i, { images })} />
                </details>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <input type="checkbox" checked={c.public !== false} onChange={(e) => setCat(i, { public: e.target.checked })} style={{ width: 16, height: 16 }} />
                    Visible to fans
                  </label>
                  <button type="button" style={{ ...btn, color: 'var(--danger, #b3261e)' }} onClick={() => set({ catalogue: cfg.catalogue.filter((_, j) => j !== i) })}>Remove</button>
                </div>
              </details>
            );
            if (!multiEvent) return cfg.catalogue.map(renderItem);
            // Several events off one Owl: fold the catalogue into one collapsible
            // section per event (events in their usual order, shared items last), so
            // it is obvious which tickets belong to which event. A section holding a
            // brand-new (unlabelled) item opens itself so the editor is never hidden.
            const groups = [...suites.map((su) => ({ key: su.id, name: su.name })), { key: '', name: 'Every event (shared: merch, parking, shuttles…)' }]
              .map((g) => ({ ...g, rows: cfg.catalogue.map((c, i) => ({ c, i })).filter(({ c }) => (c.suiteId || '') === g.key) }));
            const orphans = cfg.catalogue.map((c, i) => ({ c, i })).filter(({ c }) => c.suiteId && !suites.some((su) => su.id === c.suiteId));
            if (orphans.length) groups.push({ key: '__orphan', name: 'Event no longer on this client', rows: orphans });
            const newItemIn = (rows) => rows.some(({ c }) => !c.label);
            const addFor = (key) => set({ catalogue: [...cfg.catalogue, { label: '', kind: 'ticket', price: '', currency: 'ZAR', deepLink: '', description: '', availability: '', public: true, images: [], suiteId: key === '__orphan' ? '' : key }] });
            return groups.filter((g) => g.rows.length).map((g) => {
              const open = catGroupOpen[g.key] ?? newItemIn(g.rows);
              const soldOut = g.rows.filter(({ c }) => c.availability === 'sold out').length;
              const hidden = g.rows.filter(({ c }) => c.public === false).length;
              return (
                <details key={g.key || 'shared'} open={open} onToggle={(e) => { const isOpen = e.currentTarget.open; setCatGroupOpen((o) => ({ ...o, [g.key]: isOpen })); }}
                  style={{ ...card, paddingTop: 4, paddingBottom: 8, marginTop: 8, borderWidth: 2 }}>
                  <summary style={{ ...summaryStyle, fontSize: 14, minHeight: 40, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span aria-hidden="true" style={{ display: 'inline-block', transition: 'transform .15s', transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
                    <span style={{ flex: 1 }}>
                      <Icon name={g.key ? 'ticket' : 'folder'} size={14} /> {g.name}{' '}
                      <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· {g.rows.length} item{g.rows.length === 1 ? '' : 's'}{soldOut ? ` · ${soldOut} sold out` : ''}{hidden ? ` · ${hidden} hidden` : ''}</span>
                    </span>
                  </summary>
                  {g.rows.map(({ c, i }) => renderItem(c, i))}
                  {g.key !== '__orphan' && <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => { setCatGroupOpen((o) => ({ ...o, [g.key]: true })); addFor(g.key); }}>+ Add item to {g.key ? suiteName(g.key) : 'shared'}</button>}
                </details>
              );
            });
          })()}
          <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => set({ catalogue: [...cfg.catalogue, { label: '', kind: 'ticket', price: '', currency: 'ZAR', deepLink: '', description: '', availability: '', public: true, images: [], suiteId: multiEvent ? ticketSuite : '' }] })}>+ Add item</button>
          {saveBar}
        </>
      )}

      {tab === 'knowledge' && (
        <>
          <p style={small}>{multiEvent ? 'FAQs & policies the Owl may quote. Tag one with an event when the answer differs between them (“gates open at 4pm”); leave it on “every event” when it holds for all of them (refunds, age limits). ' : ''}Event-wide FAQs & policies that apply everywhere (refunds, age limits, what's allowed in…). Page-specific detail belongs in that page's info box under Sites & pages. Together these are the ONLY sources the Owl may quote — anything not covered gets an honest "I don't know" and logs the gap in Reports. 💡 The <strong>tip</strong> kind is insider gold the Owl may volunteer unprompted when relevant ("the east gate has no queue after 6pm").</p>
          {cfg.knowledge.map((k, i) => (
            <details key={k.id || i} style={{ ...card, paddingTop: 4, paddingBottom: 8 }} open={!k.question && !k.body}>
              <summary style={summaryStyle}>
                {k.question || (k.body ? `${k.body.slice(0, 60)}…` : 'New entry')}{' '}
                <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· {k.kind}{multiEvent ? ` · ${suiteName(k.suiteId) || 'every event'}` : ''}</span>
              </summary>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr', gap: 8 }}>
                <select style={input} value={k.kind} onChange={(e) => setKnow(i, { kind: e.target.value })}>
                  <option value="faq">FAQ</option><option value="policy">policy</option><option value="info">info</option><option value="tip">💡 tip</option>
                </select>
                <input style={input} value={k.question} placeholder="Question (e.g. What's the refund policy?)" onChange={(e) => setKnow(i, { question: e.target.value })} />
              </div>
              {multiEvent && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr', gap: 8, marginTop: 6, alignItems: 'center' }}>
                  <div style={{ ...small, margin: 0 }}>Event</div>
                  <select style={input} value={k.suiteId || ''} onChange={(e) => setKnow(i, { suiteId: e.target.value })}>
                    <option value="">— every event (refunds, age limits, directions…) —</option>
                    {suites.map((su) => <option key={su.id} value={su.id}>{su.name}</option>)}
                  </select>
                </div>
              )}
              <textarea style={{ ...input, marginTop: 6, resize: 'vertical' }} rows={3} value={k.body} placeholder="The answer, in the organiser's words — the Owl sticks closely to this." onChange={(e) => setKnow(i, { body: e.target.value })} />
              {contactTopics.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ ...small, margin: '0 0 6px' }}>Contact form topics: the “Ask a quick question” Owl for a topic answers only from entries tagged with it.</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {contactTopics.map((t) => {
                      const on = (k.topics || []).includes(t.key);
                      return (
                        <button key={t.key} type="button" aria-pressed={on} onClick={() => setKnow(i, { topics: on ? (k.topics || []).filter((x) => x !== t.key) : [...(k.topics || []), t.key] })}
                          style={{ ...btn, minHeight: 40, borderRadius: 999, ...(on ? { background: 'var(--ink, #111)', color: 'var(--bg, #fff)', borderColor: 'transparent' } : {}) }}>
                          {t.label}{t.owl ? '' : ' (Owl off)'}
                        </button>
                      );
                    })}
                  </div>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: 13, minHeight: 40 }}>
                    <input type="checkbox" checked={k.audience === 'contact'} onChange={(e) => setKnow(i, { audience: e.target.checked ? 'contact' : '' })} />
                    Contact form only (the Owl on your event pages won’t use this one)
                  </label>
                </div>
              )}
              <div style={{ textAlign: 'right', marginTop: 6 }}>
                <button type="button" style={{ ...btn, color: 'var(--danger, #b3261e)' }} onClick={() => set({ knowledge: cfg.knowledge.filter((_, j) => j !== i) })}>Remove</button>
              </div>
            </details>
          ))}
          <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => set({ knowledge: [...cfg.knowledge, { kind: 'faq', question: '', body: '', suiteId: multiEvent ? ingestSuite : '' }] })}>+ Add entry</button>
          {saveBar}
        </>
      )}

      {tab === 'whatsapp' && <FanOwlWhatsapp base={base} suites={suites} />}

      {tab === 'rewards' && (
        poolsDenied ? <p style={small}>🎁 Rewards ride the <strong>Loyalty & verification</strong> flag (Admin → Product → Flags → Fan Owl) — it's off for this client.</p>
          : !pools ? <p style={small}>Loading reward pools…</p> : (
            <RewardPools pools={pools} setPools={setPools} suites={suites} catalogue={cfg?.catalogue || []} isMobile={isMobile} loyaltyBase={loyaltyBase}
              saving={poolsSaving} savedAt={poolsSavedAt} error={poolsError} codesDraft={codesDraft} setCodesDraft={setCodesDraft} codesNote={codesNote}
              onSave={async () => {
                setPoolsSaving(true); setPoolsError('');
                try {
                  const r = await fetch(`${loyaltyBase}/pools`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pools }) });
                  const d = await r.json().catch(() => ({}));
                  if (r.ok) { setPools(d.pools || []); setPoolsSavedAt(Date.now()); }
                  else setPoolsError(d.error || `Couldn’t save the reward pools (${r.status}).`);
                } catch (e) { setPoolsError(e.message || 'Couldn’t save the reward pools.'); }
                finally { setPoolsSaving(false); }
              }}
              onUpload={async (i) => {
                const pool = pools[i];
                if (!pool.id || String(pool.id).length < 30) { setCodesNote({ i, text: 'Save the pool first, then upload its codes.' }); return; }
                const r = await fetch(`${loyaltyBase}/pools/${pool.id}/codes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ codes: codesDraft[i] || '' }) });
                const d = await r.json().catch(() => ({}));
                if (!r.ok) { setCodesNote({ i, text: `⚠️ ${d.error || 'Upload failed.'}` }); return; }
                setPools((ps) => ps.map((p, j) => (j === i ? { ...p, stock: d.stock } : p)));
                setCodesDraft((c) => ({ ...c, [i]: '' }));
                setCodesNote({ i, text: `Added ${d.added} code${d.added === 1 ? '' : 's'} ✓ (${d.stock.available} now available).` });
              }} />
          )
      )}

      {tab === 'sync' && (
        <AutoUpdate syncBase={syncBase} cfg={cfg} suites={suites} multiEvent={multiEvent} isMobile={isMobile} onPending={setSyncPending} onConfigSaved={(c) => setCfg(c)} />
      )}

      {tab === 'reports' && (
        stats ? <Flywheel stats={stats} leads={leads} base={base} trBase={scope === 'my' ? `/api/my/fan-owl-transcripts/${entityId}` : `/api/admin/entities/${entityId}/fan-owl-transcripts`} loadLeads={() => fetch(`${base}/leads`).then((r) => r.json()).then((d) => setLeads(d.leads || [])).catch(() => setLeads([]))}
          onAddFaq={async (k) => { const next = { ...cfg, knowledge: [...cfg.knowledge, { kind: k.kind || 'faq', question: k.question, body: k.body, suiteId: '' }] }; setCfg(next); return saveCfg(next); }} />
          : <p style={small}>Loading reports…</p>
      )}
    </div>
  );
}

// Downscale a picked image to a phone-friendly JPEG data-URL before upload
// (same approach as ReportForm) — keeps the payload under the server's 2MB cap.
// A comma-separated LIST in one text box. The naive version — value = the list
// joined with ', ', onChange = split + trim — can never hold a trailing space:
// every keystroke re-trims it away, so typing "Tickets on sale?" came out as
// "Ticketsonsale?" in the page starters (reported 2026-10-09). While the box has
// focus it shows the text exactly as typed and parses a fresh list on every
// keystroke (so Save mid-sentence still works); on blur it resyncs to the
// canonical join. The parse rule is unchanged: split on commas, trim, drop
// blanks, cap at `max`.
function CommaList({ value, onChange, max = 0, ...rest }) {
  const joined = (value || []).join(', ');
  const [text, setText] = useState(joined);
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setText(joined); }, [joined, editing]);
  const parse = (t) => { const out = String(t || '').split(',').map((x) => x.trim()).filter(Boolean); return max > 0 ? out.slice(0, max) : out; };
  return (
    <input {...rest} value={editing ? text : joined}
      onFocus={() => { setText(joined); setEditing(true); }}
      onChange={(e) => { setText(e.target.value); onChange(parse(e.target.value)); }}
      onBlur={() => setEditing(false)} />
  );
}

function downscaleImage(file, max = 1600, quality = 0.8, type = 'image/jpeg') {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        // An SVG without width/height reports 0×0 — draw it at the target size.
        const iw = img.width || max, ih = img.height || max;
        const scale = Math.min(1, max / Math.max(iw, ih));
        const w = Math.round(iw * scale), h = Math.round(ih * scale);
        const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL(type, quality));
      };
      img.onerror = () => reject(new Error('That file doesn’t look like an image.'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('Couldn’t read that file.'));
    reader.readAsDataURL(file);
  });
}

// ── Reward pools editor (loyalty phase 2, spec §5) ───────────────────────────────
// Budgeted pools of promo codes the Owl may grant to VERIFIED fans. Codes are
// generated in the ticketing system; this holds the stock, the target and the
// rules — issuance itself is server-side and one-per-fan.
const REWARD_KINDS_UI = [['discount', '💸 Discount'], ['upgrade', '⬆️ Upgrade'], ['addon', '➕ Add-on'], ['credit_bundle', '🍺 Ticket+credit bundle'], ['merch', '👕 Merch'], ['prize', '🏆 Prize']];
const TIER_OPTS = [['new', 'New (0 events)'], ['returning', 'Returning (1)'], ['loyal', 'Loyal (2–3)'], ['superfan', '👑 Superfan (4+)']];
const SIGNAL_OPTS = [['group_buyer', 'Group buyer (4+)'], ['comp_guest', 'Comp guest'], ['lead_no_purchase', 'Registered, never bought'], ['preregistered', 'Preregistered']];

function RewardPools({ pools, setPools, suites, catalogue, isMobile, loyaltyBase, saving, savedAt, error = '', codesDraft, setCodesDraft, codesNote, onSave, onUpload }) {
  const setPool = (i, patch) => setPools(pools.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  // Issued-codes audit per pool: who got which code, their tier, and (once the
  // ticketing redemption feed exists) whether it was redeemed.
  const [grants, setGrants] = useState({}); // pool id → rows | 'loading'
  const loadGrants = (poolId) => {
    if (grants[poolId] && grants[poolId] !== 'error') { setGrants((g) => ({ ...g, [poolId]: undefined })); return; } // toggle closed
    setGrants((g) => ({ ...g, [poolId]: 'loading' }));
    fetch(`${loyaltyBase}/pools/${poolId}/grants`).then((r) => r.json())
      .then((d) => setGrants((g) => ({ ...g, [poolId]: d.grants || [] })))
      .catch(() => setGrants((g) => ({ ...g, [poolId]: 'error' })));
  };
  const burn = (s = {}) => {
    const total = (s.available || 0) + (s.issued || 0);
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
    return (
      <div>
        <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', background: 'var(--hairline)' }}>
          <span style={{ width: `${pct(s.redeemed || 0)}%`, background: 'var(--ok, #12a150)' }} />
          <span style={{ width: `${pct(Math.max(0, (s.issued || 0) - (s.redeemed || 0)))}%`, background: 'var(--brand, #ff385c)' }} />
        </div>
        <div style={{ ...small, marginTop: 4 }}>{s.granted || 0} granted · {s.redeemed || 0} redeemed · <strong>{s.available || 0} left</strong></div>
      </div>
    );
  };
  const checks = (i, list, key) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {list.map(([val, label]) => {
        const arr = pools[i].target?.[key] || [];
        const on = arr.includes(val);
        return (
          <label key={val} style={{ fontSize: 12.5, border: '1px solid var(--hairline)', borderRadius: 999, padding: '6px 11px', cursor: 'pointer', background: on ? 'var(--text)' : 'transparent', color: on ? 'var(--bg, #fff)' : 'var(--text)' }}>
            <input type="checkbox" checked={on} style={{ display: 'none' }}
              onChange={() => setPool(i, { target: { ...pools[i].target, [key]: on ? arr.filter((x) => x !== val) : [...arr, val] } })} />
            {label}
          </label>
        );
      })}
    </div>
  );
  return (
    <>
      <p style={small}>Budgeted pools of promo codes the Owl offers to <strong>verified</strong> fans that match the target — the server grants one per fan and stops dead when the stock runs out. Codes come from the ticketing system (min-quantity, ticket types and expiry are enforced at checkout by the code itself). Empty target = every verified fan qualifies.</p>
      {pools.map((p, i) => (
        <div key={p.id || i} style={{ border: '1px solid var(--hairline)', borderRadius: 12, padding: 14, marginTop: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: 8 }}>
            <div>
              <div style={small}>Pool name</div>
              <input style={input} value={p.name} onChange={(e) => setPool(i, { name: e.target.value })} placeholder="e.g. Loyal-fan VIP upgrade" />
            </div>
            <div>
              <div style={small}>Reward kind</div>
              <select style={input} value={p.rewardKind} onChange={(e) => setPool(i, { rewardKind: e.target.value })}>
                {REWARD_KINDS_UI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div style={small}>Event (blank = any of yours)</div>
              <select style={input} value={p.suiteId || ''} onChange={(e) => setPool(i, { suiteId: e.target.value })}>
                <option value="">— portfolio-wide —</option>
                {suites.map((su) => <option key={su.id} value={su.id}>{su.name}</option>)}
              </select>
            </div>
            <div>
              <div style={small}>Value (what the fan is told)</div>
              <input style={input} value={p.valueLabel || ''} onChange={(e) => setPool(i, { valueLabel: e.target.value })} placeholder="e.g. 25% off VIP upgrade" />
            </div>
            <div>
              <div style={small}>Expiry (optional)</div>
              <input type="date" style={input} value={(p.rules?.expiresAt || '').slice(0, 10)} onChange={(e) => setPool(i, { rules: { ...p.rules, expiresAt: e.target.value } })} />
            </div>
            <div>
              <div style={small}>Comps</div>
              <select style={input} value={p.rules?.comps || 'count'} onChange={(e) => setPool(i, { rules: { ...p.rules, comps: e.target.value } })}>
                <option value="count">Comp visits count</option>
                <option value="ignore">Paid history only</option>
              </select>
            </div>
            <div>
              <div style={small}>How the code applies (same as campaigns)</div>
              <select style={input} value={p.codeType || 'discount'} onChange={(e) => setPool(i, { codeType: e.target.value })}>
                <option value="discount">🛒 Basket discount — fan copies the code at checkout</option>
                <option value="promo">🎟 Ticket promo — one-tap link (?promo=CODE)</option>
              </select>
            </div>
            {(p.codeType || 'discount') === 'promo' && (
              <div>
                <div style={small}>Ticket the link opens (needs a buy link in the Catalogue)</div>
                <select style={input} value={p.bundleItemId || ''} onChange={(e) => setPool(i, { bundleItemId: e.target.value })}>
                  <option value="">— choose a catalogue item —</option>
                  {(catalogue || []).filter((c) => c.deepLink).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            )}
          </div>
          {(p.codeType || 'discount') === 'promo' && !p.bundleItemId
            && <p style={{ ...small, color: 'var(--warn, #b3261e)', fontWeight: 700 }}>⚠️ Ticket-promo pools need a catalogue item with a buy link — until one is chosen the card falls back to tap-to-copy.</p>}
          <div style={{ ...small, marginTop: 8 }}>Who qualifies (tier — any ticked matches; each fan sits on ONE rung, so tick every rung you want — Loyal does NOT include Superfans):</div>
          {checks(i, TIER_OPTS, 'tiers')}
          <div style={{ ...small, marginTop: 6 }}>…and must have ALL of (optional):</div>
          {checks(i, SIGNAL_OPTS, 'signals')}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span style={small}>…and a streak of at least</span>
            <input type="number" min="0" max="30" style={{ ...input, width: 70 }} value={p.rules?.minStreakYears || 0}
              onChange={(e) => setPool(i, { rules: { ...p.rules, minStreakYears: Number(e.target.value) } })} />
            <span style={small}>consecutive years attended (0 = off — e.g. 3 targets the "3 years running" crowd)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginTop: 10 }}>
            <div>
              <div style={small}>Code mode</div>
              <select style={input} value={p.mode || 'unique'} onChange={(e) => setPool(i, { mode: e.target.value })}>
                <option value="unique">Unique codes (upload a stock — stock = budget)</option>
                <option value="shared">One shared multi-use code (cap the grants)</option>
              </select>
            </div>
            {p.mode === 'shared' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
                <div>
                  <div style={small}>Shared code{p.sharedCodeSet ? ' (set ✓ — retype to change)' : ''}</div>
                  <input style={input} value={p.sharedCode || ''} onChange={(e) => setPool(i, { sharedCode: e.target.value })} placeholder="e.g. BLOOM-EARLY" />
                </div>
                <div>
                  <div style={small}>Grant cap (0 = ∞)</div>
                  <input type="number" min="0" style={input} value={p.grantCap || 0} onChange={(e) => setPool(i, { grantCap: Number(e.target.value) })} />
                </div>
              </div>
            ) : (
              <div>
                <div style={small}>Add codes (paste from ticketing — one per line)</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <textarea style={{ ...input, resize: 'vertical' }} rows={1} value={codesDraft[i] || ''} onChange={(e) => setCodesDraft((c) => ({ ...c, [i]: e.target.value }))} placeholder={'VIP-A1B2\nVIP-C3D4'} />
                  <button type="button" style={btn} onClick={() => onUpload(i)}>Upload</button>
                </div>
              </div>
            )}
          </div>
          {codesNote && codesNote.i === i && <p style={small}>{codesNote.text}</p>}
          {p.active !== false && (p.mode || 'unique') === 'unique' && !(p.stock?.available > 0)
            && <p style={{ ...small, color: 'var(--warn, #b3261e)', fontWeight: 700 }}>⚠️ This pool has NO codes available — the Owl can't offer it. Save the pool, paste the codes, then hit Upload and check the count.</p>}
          {p.active !== false && p.mode === 'shared' && !p.sharedCodeSet && !String(p.sharedCode || '').trim()
            && <p style={{ ...small, color: 'var(--warn, #b3261e)', fontWeight: 700 }}>⚠️ No shared code set — the Owl can't offer this pool until you enter one and Save.</p>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>{burn(p.stock)}</div>
            {p.id && String(p.id).length > 30 && (p.stock?.granted || 0) > 0
              && <button type="button" style={btn} onClick={() => loadGrants(p.id)}>{grants[p.id] && grants[p.id] !== 'error' ? 'Hide issued codes' : `Issued codes (${p.stock.granted})`}</button>}
            <label style={{ fontSize: 12.5 }}><input type="checkbox" checked={p.active !== false} onChange={(e) => setPool(i, { active: e.target.checked })} /> Active</label>
            <button type="button" style={{ ...btn, color: 'var(--danger, #b3261e)' }} onClick={() => setPools(pools.filter((_, j) => j !== i))}>Delete pool</button>
          </div>
          {grants[p.id] === 'loading' && <p style={small}>Loading issued codes…</p>}
          {grants[p.id] === 'error' && <p style={small}>⚠️ Couldn’t load the issued codes — try again.</p>}
          {Array.isArray(grants[p.id]) && (grants[p.id].length === 0 ? <p style={small}>No codes issued yet.</p> : (
            <div style={{ overflowX: 'auto', marginTop: 8 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12.5, minWidth: 480 }}>
                <thead><tr>{['Fan', 'Tier', 'Code', 'Via', 'When', 'Redeemed'].map((h) => <th key={h} style={{ textAlign: 'left', padding: '5px 10px', borderBottom: '1px solid var(--hairline)', fontWeight: 700 }}>{h}</th>)}</tr></thead>
                <tbody>
                  {grants[p.id].map((g, gi) => (
                    <tr key={gi}>
                      <td style={{ padding: '5px 10px' }}>{g.name ? `${g.name} · ` : ''}{g.email}</td>
                      <td style={{ padding: '5px 10px' }}>{{ new: '🆕', returning: '↻', loyal: '★', superfan: '👑' }[g.tier] || ''} {g.tier}</td>
                      <td style={{ padding: '5px 10px', fontFamily: 'ui-monospace, monospace' }}>{g.code}</td>
                      <td style={{ padding: '5px 10px' }}>{g.surface}</td>
                      <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>{String(g.at || '').slice(0, 10)}</td>
                      <td style={{ padding: '5px 10px' }} title="Redemption tracking arrives with the ticketing feed">{g.redeemedAt ? `✅ ${String(g.redeemedAt).slice(0, 10)}` : '— not yet tracked'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      ))}
      <button type="button" style={{ ...btn, marginTop: 10 }} onClick={() => setPools([...pools, { name: '', rewardKind: 'discount', valueLabel: '', target: { tiers: [], signals: [] }, rules: { comps: 'count' }, mode: 'unique', active: true, stock: {} }])}>+ New pool</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
        <button type="button" style={primaryBtn} disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save reward pools'}</button>
        {savedAt > 0 && Date.now() - savedAt < 4000 && <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Saved ✓</span>}
        {error && <span style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--error,#ef4444)' }}>✕ {error}</span>}
      </div>
      <p style={{ ...small, marginTop: 6 }}>A pool’s <b>Active</b> box, like every other change here, only takes effect after <b>Save reward pools</b>; the widget drops the “perks” chip on its next open.</p>
    </>
  );
}

// The Reports window. Today / yesterday are the viewer's LOCAL days (bounds sent
// as ISO instants); the rest are rolling windows; "all" has no lower bound.
const REPORT_PERIODS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['14d', 'Last 14 days'], ['30d', 'Last 30 days'], ['all', 'All time']];
function periodQuery(p) {
  const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
  if (p === 'today') return `from=${encodeURIComponent(midnight.toISOString())}`;
  if (p === 'yesterday') { const y = new Date(midnight); y.setDate(y.getDate() - 1); return `from=${encodeURIComponent(y.toISOString())}&to=${encodeURIComponent(midnight.toISOString())}`; }
  if (p === 'all') return 'range=all';
  return `days=${parseInt(p, 10) || 30}`;
}

function Flywheel({ stats: initial, leads, loadLeads, base, trBase, onAddFaq }) {
  const [period, setPeriod] = useState('30d');
  const [convChannel, setConvChannel] = useState(''); // '' = both; a tapped channel card narrows the Conversations list
  const [stats, setStats] = useState(initial);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (period === '30d') { setStats(initial); return undefined; }
    let on = true;
    setLoading(true);
    fetch(`${base}/insights?${periodQuery(period)}`).then((r) => r.json())
      .then((s) => { if (on) setStats(s); }).catch(() => {}).finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [period, base, initial]);
  const periodLabel = (REPORT_PERIODS.find(([k]) => k === period) || [])[1] || 'Last 30 days';
  const total = useMemo(() => (stats.sites || []).reduce((acc, s) => {
    for (const [k, v] of Object.entries(s.funnel || {})) acc[k] = (acc[k] || 0) + v;
    return acc;
  }, {}), [stats]);
  const topics = useMemo(() => (stats.sites || []).flatMap((s) => (s.topics || []).map((t) => ({ ...t, siteId: s.id, siteName: s.name }))).sort((a, b) => b.c - a.c).slice(0, 10), [stats]);
  const navTaps = useMemo(() => (stats.sites || []).flatMap((s) => s.navTaps || []).sort((a, b) => b.c - a.c).slice(0, 10), [stats]);
  // Turns that failed — what the fan saw as "the Owl hit a snag", with the code
  // they were shown and the reason, which lives nowhere else a human can reach.
  const errors = useMemo(() => (stats.sites || []).flatMap((s) => (s.errors || []).map((e) => ({ ...e, site: s.name })))
    .sort((a, b) => String(b.at).localeCompare(String(a.at))).slice(0, 5), [stats]);
  const stat = (label, v) => (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '8px 12px', minWidth: 90 }}>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{v || 0}</div>
      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{label}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 8 }}>
      <div role="tablist" aria-label="Report period" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {REPORT_PERIODS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={period === k} onClick={() => setPeriod(k)}
            style={{ minHeight: 40, padding: '6px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer', border: '1px solid var(--hairline)', background: period === k ? 'var(--text, #111)' : 'transparent', color: period === k ? 'var(--bg, #fff)' : 'inherit', fontWeight: period === k ? 700 : 500 }}>
            {label}
          </button>
        ))}
      </div>
      <H>{periodLabel}{loading ? ' · loading…' : ''}</H>
      <Channels channels={stats.channels} onPick={(k) => { setConvChannel(k === 'total' ? '' : k); try { document.getElementById('fanowl-conversations')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* cosmetic */ } }} />
      {/* The widget counters are WEBSITE events (the ribbon, the chat panel, nav
          taps, buy clicks) and leads are cross-channel — neither follows the
          conversations' channel filter. They used to sit right under that list,
          so with WhatsApp picked "8178 ribbon views" read as WhatsApp numbers for
          a client who had never used it (2026-10-20). Named and placed above. */}
      <div style={{ ...small, marginTop: 12, fontWeight: 700, color: 'var(--text)' }}>Website widget · {periodLabel}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
        {stat('Page views', total.ribbon_view)}
        {stat('Visitors', total.ribbon_fans)}
        {stat('Chats opened', total.chat_open_fans)}
        {stat('Buy clicks', total.deeplink_click)}
        {stat('Nav taps', total.nav_click)}
        {total.chat_error ? stat('Failed answers', total.chat_error) : null}
      </div>
      {/* The raw counters are page loads and panel boots: the panel reloads itself
          after every question typed into the ask bar and on each page change with
          the chat open, so "3 597 chats opened" sat against 765 chats (2026-10-22).
          People are the honest unit, with the raw figures explained beside them. */}
      <p style={{ ...small, marginTop: 6 }}>
        <b>Page views</b> are pages loaded with the Owl on them; <b>Visitors</b> are the browsers that saw it.
        <b> Chats opened</b> counts a fan who opened the chat once per day, the same unit as a chat
        {total.chat_open ? <> (the panel itself loaded {total.chat_open} times: it reloads after every question typed into the ask bar and on each page change while open)</> : null}.
        A chat opened but never typed in is not a chat.
      </p>
      <div style={{ ...small, marginTop: 12, fontWeight: 700, color: 'var(--text)' }}>Leads · every channel</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
        {stat('Leads', stats.leads?.total)}
        {stat('Opted in', stats.leads?.optedIn)}
        {/* Two different proofs, so two numbers: an address confirmed by clicking
            an emailed code, and a phone confirmed by chatting on WhatsApp. One
            count covering both read as address proof and was not. */}
        {stat('Email verified', stats.leads?.emailVerified)}
        {stats.leads?.phoneVerified ? stat('Phone verified', stats.leads.phoneVerified) : null}
      </div>
      {/* Leads are fans who GAVE the Owl their details. Known fans is the wider
          Spine number — every person linked to this client from any channel —
          and is shown apart so campaign clicks never read as captured leads. */}
      <p style={{ ...small, marginTop: 8 }}>
        <b>Leads</b> are fans who left their details with the Owl (chat or the consent form).
        {stats.known?.total ? <> Separately, <b>{stats.known.total}</b> known fan{stats.known.total === 1 ? '' : 's'} are linked to you across every channel — campaign link clicks, WhatsApp campaigns and chats — {stats.known.recent || 0} of them new in this period. They are not leads and gave no consent here.</> : null}
      </p>
      <ChatThemes base={trBase.replace('fan-owl-transcripts', 'fan-owl-themes')} trBase={trBase} query={periodQuery(period)} period={period} channel={convChannel} />
      <Conversations trBase={trBase} query={periodQuery(period)} channel={convChannel} setChannel={setConvChannel} />
      {topics.length > 0 && (
        <>
          <p style={{ ...small, marginTop: 10 }}>What fans asked about — real interest and the questions the Owl couldn't answer (your FAQ gaps). Tap one to read the fans' own words and draft an FAQ from your material.</p>
          <div style={{ display: 'grid', gap: 6 }}>
            {topics.map((t) => <GapRow key={`${t.siteId}:${t.topic}`} t={t} base={base} onAddFaq={onAddFaq} />)}
          </div>
        </>
      )}
      {navTaps.length > 0 && (
        <>
          <p style={{ ...small, marginTop: 10 }}>Where fans navigated (nav-button taps):</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {navTaps.map((t) => <span key={t.path} style={{ fontSize: 12, border: '1px solid var(--hairline)', borderRadius: 999, padding: '4px 10px', fontFamily: 'ui-monospace, monospace' }}>{t.path} · {t.c}</span>)}
          </div>
        </>
      )}
      {errors.length > 0 && (
        <>
          <p style={{ ...small, marginTop: 10 }}>
            Questions the Owl couldn’t answer — the fan saw “the Owl hit a snag” with this code.
            Quote the code to Howler and we can see the full detail behind it.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {errors.map((e) => (
              <div key={`${e.ref}-${e.at}`} style={{ fontSize: 12, border: '1px solid var(--hairline)', borderRadius: 10, padding: '6px 10px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{e.ref || '—'}</span>
                  {/* A spend cap is not a fault to debug — say so plainly, in the one
                      place someone would look, instead of "400 invalid_request_error". */}
                  {e.problem === 'quota'
                    ? <span style={{ fontWeight: 700, color: 'var(--warn, #b45309)', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="warn" /> AI budget reached — the Owl can’t answer anyone until it’s raised</span>
                    : <span>{[e.status, e.type].filter(Boolean).join(' · ') || 'no detail recorded'}</span>}
                  <span style={{ color: 'var(--muted)' }}>{String(e.at || '').replace('T', ' ').slice(0, 16)}{e.site ? ` · ${e.site}` : ''}</span>
                </div>
                {e.detail && <div style={{ marginTop: 4, wordBreak: 'break-word' }}>{e.detail}</div>}
                {e.shape && <div style={{ marginTop: 3, color: 'var(--muted)', fontFamily: 'ui-monospace, monospace', fontSize: 11, wordBreak: 'break-word' }}>{e.shape}</div>}
              </div>
            ))}
          </div>
        </>
      )}
      <div style={{ marginTop: 10 }}>
        {!leads && <button type="button" style={btn} onClick={loadLeads}>Show captured leads</button>}
        {leads && (leads.length === 0 ? <p style={small}>No leads captured yet — only fans who leave their details with the Owl appear here.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12.5, minWidth: 480 }}>
              <thead><tr>{['Email', 'Name', 'Verified', 'Tier', 'Marketing opt-in', 'Interests', 'When'].map((h) => <th key={h} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--hairline)', fontWeight: 700 }}>{h}</th>)}</tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td style={{ padding: '6px 10px' }}>{l.email}</td>
                    <td style={{ padding: '6px 10px' }}>{l.name}</td>
                    {/* Say WHICH proof — a ✓ that means two different things is not a ✓. */}
                    <td style={{ padding: '6px 10px' }} title={l.verified ? (l.verifiedChannel === 'whatsapp' ? 'Phone confirmed on WhatsApp — their email address is not proven' : 'Email address confirmed') : 'Not verified'}>{l.verified ? (l.verifiedChannel === 'whatsapp' ? '✓ phone' : '✓ email') : '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{l.tier ? { new: '🆕 new', returning: '↻ returning', loyal: '★ loyal', superfan: '👑 superfan' }[l.tier] || l.tier : '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{l.consentMarketing ? '✅' : '—'}</td>
                    <td style={{ padding: '6px 10px' }}>{(l.preferences || []).join(', ')}</td>
                    <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{String(l.at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

// The chats behind the channel numbers (one fan's messages on one day), newest
// first; tap one to read the transcript in place. Flag fanowl.transcripts — the
// client route is gated, so a 403 simply hides the section.
const CONV_CHANNELS = [['', 'All'], ['web', 'Website'], ['whatsapp', 'WhatsApp']];
function Conversations({ trBase, query, channel, setChannel }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [openKey, setOpenKey] = useState('');
  const [shown, setShown] = useState(30);
  useEffect(() => {
    let on = true;
    setData(null); setOpenKey(''); setShown(30);
    fetch(`${trBase}?${query}${channel ? `&channel=${channel}` : ''}`)
      .then((r) => { if (r.status === 403) { if (on) setHidden(true); return null; } return r.json(); })
      .then((d) => { if (on && d) { setHidden(false); setData(d); } })
      .catch(() => { if (on) setData({ chats: [] }); });
    return () => { on = false; };
  }, [trBase, query, channel]);
  if (hidden) return null;
  const chats = data?.chats || [];
  return (
    <div id="fanowl-conversations" style={{ marginTop: 14 }}>
      <H>Conversations{data ? ` · ${chats.length}${chats.length >= 300 ? '+' : ''}` : ''}</H>
      {data && data.flagOn === false && <p style={{ ...small, color: 'var(--warn, #b45309)' }}>Staff view only — transcripts are switched off for this client (Admin → Product → Flags → Fan Owl → Conversation transcripts).</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '6px 0 8px' }}>
        {CONV_CHANNELS.map(([k, label]) => (
          <button key={k || 'all'} type="button" onClick={() => setChannel(k)} aria-pressed={channel === k}
            style={{ minHeight: 40, padding: '6px 14px', borderRadius: 999, fontSize: 13, cursor: 'pointer', border: '1px solid var(--hairline)', background: channel === k ? 'var(--text)' : 'transparent', color: channel === k ? 'var(--bg, #fff)' : 'inherit', fontWeight: channel === k ? 700 : 500 }}>
            {label}
          </button>
        ))}
      </div>
      {!data && <p style={small}>Loading conversations…</p>}
      {data && chats.length === 0 && <p style={small}>No conversations in this period.</p>}
      <div style={{ display: 'grid', gap: 6 }}>
        {chats.slice(0, shown).map((c) => {
          const key = `${c.sessionId}:${c.day}`;
          return <ConversationRow key={key} c={c} trBase={trBase} open={openKey === key} onToggle={() => setOpenKey(openKey === key ? '' : key)} />;
        })}
      </div>
      {chats.length > shown && <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => setShown(shown + 30)}>Show more ({chats.length - shown} left)</button>}
      <p style={{ ...small, marginTop: 6 }}>WhatsApp numbers are partly hidden unless the fan left their details with the Owl (a lead).</p>
    </div>
  );
}

function ConversationRow({ c, trBase, open, onToggle }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (!open || t) return undefined;
    let on = true;
    fetch(`${trBase}/${encodeURIComponent(c.sessionId)}?day=${c.day}`).then((r) => r.json())
      .then((d) => { if (on) setT(d); }).catch(() => { if (on) setT({ messages: [] }); });
    return () => { on = false; };
  }, [open, t, trBase, c.sessionId, c.day]);
  const when = String(c.lastAt || '') ? new Date(c.lastAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 10 }}>
      <button type="button" onClick={onToggle} aria-expanded={open}
        style={{ width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', background: 'transparent', border: 0, padding: '10px 12px', cursor: 'pointer', minHeight: 44 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <Icon name={c.channel === 'whatsapp' ? 'chat' : 'web'} size={14} />
          <b style={{ wordBreak: 'break-word' }}>{c.who?.label}</b>
          {c.who?.lead && <span style={{ fontSize: 11, border: '1px solid var(--hairline)', borderRadius: 999, padding: '1px 8px' }}>lead</span>}
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>{when} · {c.messages} msg{c.messages === 1 ? '' : 's'}{c.site ? ` · ${c.site}` : ''}</span>
        </div>
        {c.firstQuestion && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>“{c.firstQuestion}”</div>}
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--hairline)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!t && <p style={small}>Loading transcript…</p>}
          {t && (t.who?.email || t.who?.phone) && <p style={small}>{[t.who.email, t.who.phone].filter(Boolean).join(' · ')}</p>}
          {t && (t.messages || []).map((m, i) => (
            <div key={i} style={{ alignSelf: m.from === 'fan' ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', padding: '8px 11px', borderRadius: 12, border: '1px solid var(--hairline)', background: m.from === 'fan' ? 'var(--surface-2, rgba(0,0,0,0.04))' : 'transparent' }}>{m.body}</div>
              <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 2, textAlign: m.from === 'fan' ? 'right' : 'left' }}>
                {m.from === 'fan' ? 'Fan' : 'Owl'} · {String(m.at || '') ? new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}{m.tools?.length ? ` · used ${m.tools.join(', ')}` : ''}
              </div>
            </div>
          ))}
          {t && !(t.messages || []).length && <p style={small}>No messages to show.</p>}
        </div>
      )}
    </div>
  );
}

// One topic from the Reports tab: the count, who raised it (interest vs a
// question the Owl couldn't answer), the fans' own wording, and a one-tap
// grounded FAQ draft the organiser edits and adds. Mobile-first: everything
// stacks; the draft is a plain form under the row.
function GapRow({ t, base, onAddFaq }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null); // { kind, question, body, sourced, hint }
  const [note, setNote] = useState('');
  const gaps = t.kinds?.faq_gap || 0; const interest = t.kinds?.interest || 0;
  const who = [gaps ? `${gaps} unanswered` : '', interest ? `${interest} interested` : ''].filter(Boolean).join(' · ');
  const draftIt = async () => {
    setBusy(true); setNote('');
    try {
      const r = await fetch(`${base}/gaps/suggest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ siteId: t.siteId, topic: t.topic, examples: t.examples || [] }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'The draft failed');
      setDraft({ kind: d.kind || 'faq', question: d.question || t.topic, body: d.body || '', sourced: !!d.sourced, hint: d.hint || '' });
    } catch (e) { setNote(`⚠ ${e.message}`); }
    setBusy(false);
  };
  const add = async () => {
    if (!draft.question.trim() || !draft.body.trim()) { setNote('Give it a question and an answer first.'); return; }
    setBusy(true); setNote('');
    try { const ok = await onAddFaq(draft); setNote(ok ? 'Added to your FAQs ✓ — the Owl uses it from the next chat.' : 'Save failed — try again.'); if (ok) setDraft(null); }
    catch (e) { setNote(`⚠ ${e.message}`); }
    setBusy(false);
  };
  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '8px 12px' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={{ ...btn, border: 'none', padding: 0, width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 32 }} aria-expanded={open}>
        <span style={{ fontSize: 13 }}><strong>{t.topic}</strong> · {t.c}{t.siteName ? <span style={{ color: 'var(--muted)' }}> · {t.siteName}</span> : null}</span>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{who}{open ? ' ▴' : ' ▾'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
          {(t.examples || []).length
            ? <div style={{ display: 'grid', gap: 4 }}>{t.examples.map((q) => <div key={q} style={{ fontSize: 12.5, borderLeft: '3px solid var(--hairline)', paddingLeft: 8, color: 'var(--text)' }}>“{q}”</div>)}</div>
            : <p style={small}>No wording captured for this one — the Owl only logged the topic.</p>}
          {!draft && <div><button type="button" style={btn} disabled={busy} onClick={draftIt}>{busy ? 'Drafting…' : 'Draft an FAQ from my material'}</button></div>}
          {draft && (
            <div style={{ display: 'grid', gap: 6 }}>
              {!draft.sourced && <p style={{ ...small, color: 'var(--warn, #b45309)', fontWeight: 600 }}>Nothing in your FAQs, pages or catalogue answers this yet — {draft.hint || 'write the answer in your own words'}. The Owl never guesses, so it stays a gap until you fill it in.</p>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <select style={{ ...input, width: 'auto' }} value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                  <option value="faq">FAQ</option><option value="policy">policy</option><option value="info">info</option>
                </select>
                <input style={{ ...input, flex: '1 1 220px' }} value={draft.question} placeholder="The question, in a fan's words" onChange={(e) => setDraft({ ...draft, question: e.target.value.slice(0, 300) })} />
              </div>
              <textarea style={{ ...input, resize: 'vertical' }} rows={3} value={draft.body} placeholder="The answer — real prices, times and rules only" onChange={(e) => setDraft({ ...draft, body: e.target.value.slice(0, 4000) })} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" style={{ ...btn, fontWeight: 700 }} disabled={busy} onClick={add}>{busy ? 'Saving…' : 'Add to FAQs'}</button>
                <button type="button" style={btn} disabled={busy} onClick={() => setDraft(null)}>Discard</button>
              </div>
            </div>
          )}
          {note && <p style={{ ...small, color: note.startsWith('⚠') ? 'var(--danger, #b3261e)' : 'var(--success, #10b981)' }}>{note}</p>}
        </div>
      )}
    </div>
  );
}

// ─── Auto-update — re-read the website & ticket shop on a schedule ──────────
// Sources are the same reads as "Read the website" / "Read the ticket site",
// remembered and repeated (server/fanOwlSync.js): what differs comes back as
// PENDING changes a person applies or dismisses, or — on "apply automatically" —
// lands on the spot (prices, availability, buy links, page info, new FAQs).
// An item that vanished from the shop is only ever HIDDEN, and only by a person.
// Icons come from <Icon> (docs/ICON_SYSTEM.md); the UI carries no emoji of its own.
const CADENCES = [['daily', 'Every day'], ['weekly', 'Every week'], ['off', 'Paused']];
const MODES = [['review', 'Review first — show me what changed'], ['auto', 'Apply automatically (vanished items still wait for me)']];
const when = (iso) => { if (!iso) return ''; const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); };
const STATUS_LABEL = { pending: 'waiting for review', applied: 'applied', 'applied-partly': 'partly applied', unchanged: 'no change on the site', nothing: 'nothing new', error: 'failed', dismissed: 'dismissed', superseded: 'replaced by a newer read' };
function changeLine(c) {
  const fieldWords = (fields) => Object.entries(fields || {}).map(([k, v]) => {
    if (k === 'price') return `price ${v.from || '—'} → ${v.to}`;
    if (k === 'currency') return `currency ${v.from || '—'} → ${v.to}`;
    if (k === 'availability') return `availability ${v.from || 'available'} → ${v.to || 'available'}`;
    if (k === 'deepLink') return 'buy link updated';
    if (k === 'description') return v.from ? (v.fact ? 'stale status removed from the description' : `description → “${String(v.to).slice(0, 80)}${String(v.to).length > 80 ? '…' : ''}”`) : 'description filled in';
    if (k === 'images') return 'images added';
    if (k === 'content') return 'page info refreshed';
    if (k === 'note') return 'note filled in';
    if (k === 'starters') return 'starter questions added';
    return k;
  }).join(', ');
  if (c.target === 'item') {
    if (c.type === 'add') return `New item: ${c.label}${c.item?.price ? ` · ${c.item.currency || ''} ${c.item.price}` : ''}`;
    if (c.type === 'missing') return `${c.label} — no longer on the site, hide it`;
    return `Ticket ${c.label}: ${fieldWords(c.fields)}`;
  }
  if (c.target === 'page') return c.type === 'add' ? `New page: ${c.label}` : `Page ${c.label}: ${fieldWords(c.fields)}`;
  return `New FAQ: ${c.label}`;
}
function AutoUpdate({ syncBase, cfg, suites, multiEvent, isMobile, onPending, onConfigSaved }) {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [picks, setPicks] = useState({}); // runId → Set of change indexes to apply
  const load = (d) => {
    setData(d); setDraft(d.sources.map((x) => ({ ...x })));
    const pending = d.runs.filter((r) => r.status === 'pending');
    onPending(pending.length);
    setPicks((p) => { const n = { ...p }; for (const r of pending) if (!n[r.id]) n[r.id] = new Set(r.changes.map((c, i) => (c.applied ? -1 : i)).filter((i) => i >= 0)); return n; });
  };
  useEffect(() => {
    let on = true;
    fetch(syncBase).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status))))).then((d) => { if (on) load(d); })
      .catch((e) => { if (on && e.message === '403') setDenied(true); });
    return () => { on = false; };
  }, [syncBase]); // eslint-disable-line react-hooks/exhaustive-deps
  const call = async (path, body, label) => {
    setBusy(label); setNote('');
    try {
      const r = await fetch(`${syncBase}${path}`, { method: body === undefined ? 'GET' : (path === '' ? 'PUT' : 'POST'), headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'That didn’t work — try again.');
      load(d);
      return d;
    } catch (e) { setNote(e.message); return null; }
    finally { setBusy(''); }
  };
  if (denied) return <p style={small}>Auto-update isn’t enabled for this account yet — ask Howler to switch on “Auto-update from the website & ticket shop” for you.</p>;
  if (!data || !draft) return <p style={small}>Loading…</p>;
  const setSrc = (i, patch) => setDraft(draft.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const add = (kind) => setDraft([...draft, { kind, siteId: (cfg.sites[0] || {}).id || '', suiteId: '', url: kind === 'website' ? `https://${((cfg.sites[0] || {}).domains || [])[0] || ''}` : '', cadence: 'daily', mode: 'review' }]);
  const siteName = (id) => (cfg.sites.find((x) => x.id === id) || {}).name || 'Untitled site';
  const suiteName = (id) => (suites.find((su) => su.id === id) || {}).name || '';
  const sourceLabel = (id) => { const x = data.sources.find((y) => y.id === id); return x ? `${x.kind === 'catalogue' ? 'Ticket shop' : `Website — ${siteName(x.siteId)}`}${multiEvent && x.suiteId ? ` · ${suiteName(x.suiteId)}` : ''} — ${x.url}` : 'a removed source'; };
  const pending = data.runs.filter((r) => r.status === 'pending');
  const recent = data.runs.filter((r) => r.status !== 'pending').slice(0, 8);
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.sources);
  return (
    <div>
      <h3 style={{ fontSize: 14, fontWeight: 700, margin: '2px 0 4px', display: 'flex', alignItems: 'center', gap: 7 }}><Icon name="repeat" size={16} />Auto-update</h3>
      <p style={small}>Keep the Owl in step with the site: the same “Read the website” and “Read the ticket site” reads, run for you every day or week. The model only drafts when the page text actually changed, and nothing is ever deleted — an item that vanished from the shop waits for you to hide it.</p>
      {!data.flagOn && <p style={{ ...small, color: 'var(--warn, #b45309)' }}>Auto-update is switched off for this client (Admin → Product → Flags → Fan Owl), so these sources keep their settings but won’t run. “Run now” still works.</p>}

      {pending.length > 0 && (
        <div style={{ ...card, borderColor: 'var(--text)' }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Icon name="pending" size={15} />Waiting for you — {pending.length} re-read{pending.length === 1 ? '' : 's'} found changes</div>
          {pending.map((r) => {
            const set = picks[r.id] || new Set();
            const open = r.changes.map((c, i) => (c.applied ? -1 : i)).filter((i) => i >= 0);
            return (
              <div key={r.id} style={{ borderTop: '1px dashed var(--hairline)', padding: '8px 0' }}>
                <div style={{ fontSize: 12.5, fontWeight: 600 }}>{sourceLabel(r.sourceId)}</div>
                <div style={small}>{when(r.at)} · {r.summary}</div>
                <div style={{ display: 'grid', gap: 4, margin: '6px 0' }}>
                  {r.changes.map((c, i) => (
                    <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, minHeight: 40, padding: '4px 0', opacity: c.applied ? 0.6 : 1 }}>
                      <input type="checkbox" disabled={!!c.applied} checked={c.applied ? true : set.has(i)} onChange={(e) => setPicks({ ...picks, [r.id]: new Set(e.target.checked ? [...set, i] : [...set].filter((x) => x !== i)) })} style={{ marginTop: 3 }} />
                      <span>{changeLine(c)}{c.applied ? ' · applied' : ''}</span>
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" style={primaryBtn} disabled={!!busy || !set.size} onClick={async () => { const d = await call(`/runs/${r.id}/apply`, { accept: [...set] }, r.id); if (d?.config) { onConfigSaved(d.config); setNote(`Applied ${set.size} of ${open.length} change${open.length === 1 ? '' : 's'} — the catalogue, pages and FAQs are saved.`); } }}>{busy === r.id ? 'Applying…' : `Apply ${set.size} selected`}</button>
                  <button type="button" style={btn} disabled={!!busy} onClick={() => call(`/runs/${r.id}/dismiss`, {}, `d${r.id}`)}>Dismiss</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ fontWeight: 700, fontSize: 13.5, margin: '14px 0 2px' }}>Sources</div>
      {!draft.length && <p style={small}>No sources yet. Add the event website (feeds Pages & FAQs) and the ticket shop (feeds the Catalogue).</p>}
      {draft.map((x, i) => (
        <div key={x.id || `new-${i}`} style={card}>
          <div style={{ display: 'grid', gap: 8 }}>
            <select style={input} value={x.kind} onChange={(e) => setSrc(i, { kind: e.target.value })}>
              <option value="website">Website — pages, info, chips &amp; FAQs</option>
              <option value="catalogue">Ticket shop — the catalogue (prices, availability, buy links)</option>
            </select>
            {x.kind === 'website' && (
              <select style={input} value={x.siteId || ''} onChange={(e) => setSrc(i, { siteId: e.target.value })}>
                <option value="">— which site do these pages belong to? —</option>
                {cfg.sites.map((st) => <option key={st.id} value={st.id}>{st.name || 'Untitled site'}</option>)}
              </select>
            )}
            {multiEvent && (
              <select style={input} value={x.suiteId || ''} onChange={(e) => setSrc(i, { suiteId: e.target.value })}>
                <option value="">— every event (shared) —</option>
                {suites.map((su) => <option key={su.id} value={su.id}>{su.name}</option>)}
              </select>
            )}
            <input style={input} value={x.url} placeholder={x.kind === 'website' ? 'https://your-event-site.com' : 'https://howler.co.za/events/…'} onChange={(e) => setSrc(i, { url: e.target.value })} />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
              <select style={input} value={x.cadence} onChange={(e) => setSrc(i, { cadence: e.target.value })}>{CADENCES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
              <select style={input} value={x.mode} onChange={(e) => setSrc(i, { mode: e.target.value })}>{MODES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            </div>
          </div>
          {x.id && (
            <p style={{ ...small, marginTop: 6 }}>
              {x.lastRunAt ? <>Last read {when(x.lastRunAt)} · {STATUS_LABEL[x.lastStatus] || x.lastStatus}{x.lastSummary ? ` — ${x.lastSummary}` : ''}</> : 'Not read yet'}
              {x.cadence !== 'off' && x.nextRunAt ? ` · next ${when(x.nextRunAt)}` : ''}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {x.id && <button type="button" style={{ ...btn, fontWeight: 700 }} disabled={!!busy || dirty} title={dirty ? 'Save your source changes first' : ''} onClick={() => call(`/run/${x.id}`, {}, `run${x.id}`)}>{busy === `run${x.id}` ? 'Reading… (~30–60s)' : 'Run now'}</button>}
            <button type="button" style={btn} disabled={!!busy} onClick={() => setDraft(draft.filter((_, j) => j !== i))}>Remove</button>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        <button type="button" style={btn} onClick={() => add('website')}>+ Website source</button>
        <button type="button" style={btn} onClick={() => add('catalogue')}>+ Ticket-shop source</button>
        <button type="button" style={primaryBtn} disabled={!!busy || !dirty} onClick={() => call('', { sources: draft }, 'save')}>{busy === 'save' ? 'Saving…' : 'Save sources'}</button>
      </div>
      {note && <p style={{ ...small, marginTop: 6 }}>{note}</p>}

      {recent.length > 0 && (
        <details style={{ marginTop: 14 }}>
          <summary style={summaryStyle}>Recent reads</summary>
          {recent.map((r) => <div key={r.id} style={{ ...small, borderTop: '1px dashed var(--hairline)', padding: '6px 0' }}>{when(r.at)} · {STATUS_LABEL[r.status] || r.status} · {sourceLabel(r.sourceId)}<br />{r.summary}</div>)}
        </details>
      )}
    </div>
  );
}
